import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import '../styles/AdminDashboard.css';
import { registerAdminPush, isPushAvailable } from '../utils/pushRegistration.js';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, BarElement, CategoryScale, Legend, LinearScale, Tooltip);

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';  // empty = same origin (Vite proxies /socket.io)
const ADMIN_TOKEN_KEY = 'einv_admin_token';
const ADMIN_NOTICE_KEY = 'einv_admin_notice';
const ADMIN_APP_BASE_PATH = '/admin/invitations';
const ADMIN_API_BASE_PATH = '/admin/invitations';
const ADMIN_CHECKER_DEEPLINK_KEY = 'seat_checker_admin_deeplink';
const ADMIN_ACTIVE_TAB_KEY = 'einv_admin_active_tab';
const ADMIN_BOOKING_VIEW_KEY = 'einv_admin_booking_view';
const ADMIN_TAB_VALUES = new Set(['invitations', 'bookings', 'reviews']);
const BOOKING_VIEW_VALUES = new Set(['active', 'archived']);

const INVITATION_CATEGORY_OPTIONS = [
  { value: 'birthday', label: 'Birthday', emailExample: 'birthdays@yourdomain.com' },
  { value: 'birthday-christening', label: 'Birthday & Christening', emailExample: 'birthday-christening@yourdomain.com' },
  { value: 'christening', label: 'Christening', emailExample: 'christening@yourdomain.com' },
  { value: 'debut', label: 'Debut', emailExample: 'debut@yourdomain.com' },
  { value: 'wedding', label: 'Wedding', emailExample: 'wedding@yourdomain.com' },
  { value: 'anniversary', label: 'Anniversary', emailExample: 'anniversary@yourdomain.com' },
  { value: 'corporate', label: 'Corporate', emailExample: 'corporate@yourdomain.com' },
  { value: 'promotion', label: 'Promotion', emailExample: 'promo@yourdomain.com' },
  { value: 'other', label: 'Other', emailExample: 'events@yourdomain.com' }
];

const DASHBOARD_PALETTE = ['#7c2d12', '#b45309', '#d97706', '#4d7c0f', '#0f766e', '#1d4ed8', '#9333ea', '#be185d'];
const CHART_GRID_COLOR = 'rgba(124,45,18,0.12)';
const CHART_TEXT_COLOR = '#5c4b3f';
const BOOKING_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', tone: 'amber' },
  { value: 'in_progress', label: 'In Progress', tone: 'blue' },
  { value: 'completed', label: 'Completed', tone: 'green' },
  { value: 'cancelled', label: 'Cancelled', tone: 'red' }
];

function getApi(path) { return `${API_BASE_URL}${path}`; }

function capitalize(str) {
  return String(str || '').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getBookingStatusOption(status) {
  return BOOKING_STATUS_OPTIONS.find((option) => option.value === status) || BOOKING_STATUS_OPTIONS[0];
}

function getAdminTab(value) {
  const normalized = String(value || '').toLowerCase();
  return ADMIN_TAB_VALUES.has(normalized) ? normalized : '';
}

function getBookingView(value) {
  const normalized = String(value || '').toLowerCase();
  return BOOKING_VIEW_VALUES.has(normalized) ? normalized : '';
}

function normalizeInvitationId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function toDesignFileName(invitationId) {
  const id = normalizeInvitationId(invitationId);
  return id ? `${id}.jsx` : '<invitation-id>.jsx';
}

function toTitleCaseFromId(invitationId) {
  const text = String(invitationId || '').replace(/-/g, ' ').trim();
  if (!text) return '';
  return text.replace(/\b\w/g, (m) => m.toUpperCase());
}

function toComponentName(invitationId) {
  const baseName = String(invitationId || '')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join('') || 'CustomInvitation';

  return /^[0-9]/.test(baseName) ? `Invitation${baseName}` : baseName;
}

function buildClientGeneratedDesignSource(invitationId, title, category, rsvpEmail) {
  const normalizedId = normalizeInvitationId(invitationId) || '<invitation-id>';
  const safeTitle = String(title || toTitleCaseFromId(normalizedId)).trim() || toTitleCaseFromId(normalizedId);
  const normalizedCategory = String(category || '').trim() || 'uncategorized';
  const previewPath = `/invitations/custom/${normalizedId}`;
  const componentName = toComponentName(normalizedId);
  const safeRsvp = String(rsvpEmail || '').trim().toLowerCase();

  return `import React, { useState, useEffect } from 'react';
import { useRsvpOpen } from '../../../utils/rsvpStatus';

const INVITATION_ID = ${JSON.stringify(normalizedId)};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
function getApi(path) { return API_BASE_URL + path }

const invitationConfig = {
  invitationId: INVITATION_ID,
  invitationCategory: ${JSON.stringify(normalizedCategory)},
  invitationTitle: ${JSON.stringify(safeTitle)},
  rsvpEmail: ${JSON.stringify(safeRsvp)},
  previewPath: ${JSON.stringify(previewPath)}
};

export default function ${componentName}() {
  const rsvpOpen = useRsvpOpen(INVITATION_ID);
  const [showRsvp, setShowRsvp] = useState(false);

  // Dev-only: auto-integrate when previewing locally
  useEffect(() => {
    try {
      if (import.meta.env && import.meta.env.MODE !== 'development') return;
      if (!INVITATION_ID) return;
      const endpoint = (import.meta.env.VITE_API_BASE_URL || '') + '/internal/dev/invitations/' + encodeURIComponent(INVITATION_ID) + '/auto-integrate';
      fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ environment: 'local' }) })
        .then(res => res.json()).then(json => { if (!json || !json.ok) console.warn('Dev auto-integrate failed', json) }).catch(() => {});
    } catch (e) { /* ignore */ }
  }, []);

  return (
    <main style={{ minHeight: '100vh', background: '#f6f1e8', padding: '32px 16px', fontFamily: 'Georgia, serif' }}>
      <section style={{ maxWidth: '760px', margin: '0 auto', background: '#fff', borderRadius: '16px', boxShadow: '0 12px 30px rgba(0,0,0,0.08)', padding: '32px' }}>
        <p style={{ margin: 0, fontSize: '12px', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b7765' }}>
          Starter Invitation File
        </p>
        <h1 style={{ margin: '12px 0 0', color: '#3a2c22' }}>${safeTitle}</h1>
        <p style={{ marginTop: '12px', color: '#6b5a4c', lineHeight: 1.7 }}>
          This JSX file was generated by Invitation Admin. Update the layout, content, styles, and RSVP form here.
        </p>

        <div style={{ marginTop: '24px', display: 'grid', gap: '10px', padding: '18px', borderRadius: '12px', background: '#f9f5ee', border: '1px solid #eadfcd', color: '#3a2c22' }}>
          <p style={{ margin: 0 }}><strong>Invitation ID:</strong> ${normalizedId}</p>
          <p style={{ margin: 0 }}><strong>Category:</strong> ${normalizedCategory}</p>
          <p style={{ margin: 0 }}><strong>RSVP Email:</strong> ${safeRsvp}</p>
          <p style={{ margin: 0 }}><strong>Preview Route:</strong> ${previewPath}</p>
        </div>

        <div style={{ marginTop: '24px' }}>
          {rsvpOpen ? (
            <button
              onClick={() => setShowRsvp(true)}
              style={{ padding: '12px 28px', background: '#7c2d12', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px' }}
            >
              RSVP Now
            </button>
          ) : (
            <p style={{ color: '#9a3412', fontStyle: 'italic' }}>RSVPs are now closed.</p>
          )}
        </div>

        {showRsvp && (
          <div style={{ marginTop: '20px', padding: '20px', border: '1px solid #e5d5c0', borderRadius: '12px' }}>
            <h3 style={{ marginTop: 0 }}>RSVP Form</h3>
            <p style={{ color: '#6b5a4c' }}>Replace this placeholder with your real RSVP form. The invitation ID is already wired up.</p>
            <button onClick={() => setShowRsvp(false)} style={{ marginTop: '8px', padding: '8px 18px', cursor: 'pointer' }}>Close</button>
          </div>
        )}

        <div style={{ marginTop: '24px', padding: '18px', borderRadius: '12px', border: '1px dashed #cdbca8', color: '#6b5a4c', lineHeight: 1.7 }}>
          <p style={{ marginTop: 0 }}>Suggested next steps:</p>
          <p style={{ margin: '8px 0 0' }}>1. Replace this starter section with your real invitation design.</p>
          <p style={{ margin: '8px 0 0' }}>2. Keep INVITATION_ID aligned with the admin record for RSVP and seat-checker data.</p>
          <p style={{ margin: '8px 0 0' }}>3. The useRsvpOpen hook is already wired — it auto-hides the RSVP button after the event date or when admin closes RSVP.</p>
          <p style={{ margin: '8px 0 0' }}>4. Set the event date in Admin &gt; Invitations &gt; Edit to enable automatic RSVP close.</p>
          <p style={{ margin: '8px 0 0' }}>5. Rebuild the app after editing if you are preparing a production deployment.</p>
        </div>
      </section>
    </main>
  );
}
`;
}

function StatCard({ label, value, color }) {
  return (
    <div className="adm-stat-card" style={{ borderTopColor: color }}>
      <div className="adm-stat-label">{label}</div>
      <div className="adm-stat-value" style={{ color }}>{value}</div>
    </div>
  );
}

function BookingStatusMenu({ status, disabled, readOnly = false, isOpen, onToggle, onChange }) {
  const current = getBookingStatusOption(status);
  const isBusy = disabled && !readOnly;
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const [openUpward, setOpenUpward] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen || !listRef.current || !menuRef.current) {
      setOpenUpward(false);
      return undefined;
    }

    const updatePlacement = () => {
      if (!listRef.current || !menuRef.current) return;

      const triggerRect = menuRef.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const menuHeight = listRect.height + 8;

      setOpenUpward(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen]);

  return (
    <div
      ref={menuRef}
      className={`adm-status-menu adm-status-menu--${current.tone}${isOpen ? ' open' : ''}${openUpward ? ' open-upward' : ''}${readOnly ? ' read-only' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="adm-status-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled || readOnly}
        onClick={disabled || readOnly ? undefined : onToggle}
      >
        <span className="adm-status-dot" aria-hidden="true"></span>
        <span className="adm-status-label">{current.label}</span>
        {isBusy ? <span className="adm-status-spinner" aria-hidden="true"></span> : (!readOnly && <span className="adm-status-chevron" aria-hidden="true"></span>)}
      </button>

      {isOpen && !disabled && !readOnly && (
        <div ref={listRef} className="adm-status-list" role="menu">
          {BOOKING_STATUS_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`adm-status-option adm-status-option--${option.tone}${option.value === status ? ' selected' : ''}`}
              role="menuitemradio"
              aria-checked={option.value === status}
              onClick={() => onChange(option.value)}
            >
              <span className="adm-status-dot" aria-hidden="true"></span>
              <span>{option.label}</span>
              {option.value === status && <span className="adm-status-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSeatChecker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getAdminTab(searchParams.get('tab')) || getAdminTab(localStorage.getItem(ADMIN_ACTIVE_TAB_KEY)) || 'invitations';
  const bookingView = getBookingView(searchParams.get('bookingView')) || getBookingView(localStorage.getItem(ADMIN_BOOKING_VIEW_KEY)) || 'active';

  /* ── Auth ─────────────────────────────────────────────── */
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null); // { type: 'success'|'error'|'info', msg: string }
  const toastTimerRef = React.useRef(null);
  const [authMessage, setAuthMessage] = useState(() => {
    const msg = sessionStorage.getItem(ADMIN_NOTICE_KEY) || '';
    if (msg) sessionStorage.removeItem(ADMIN_NOTICE_KEY);
    return msg;
  });

  /* ── Invitations ──────────────────────────────────────── */
  const [rows, setRows] = useState([]);
  const [invitationId, setInvitationId] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [sourceBookingId, setSourceBookingId] = useState(null);
  const [newInvitationId, setNewInvitationId] = useState('');
  const [newCategory, setNewCategory] = useState(INVITATION_CATEGORY_OPTIONS[0].value);
  const [newTitle, setNewTitle] = useState('');
  const [newRsvpEmail, setNewRsvpEmail] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [offcanvasError, setOffcanvasError] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [generatedFileName, setGeneratedFileName] = useState('');
  const [generatedPreviewPath, setGeneratedPreviewPath] = useState('');
  const [generatedFileCreated, setGeneratedFileCreated] = useState(false);
  const [showGeneratedGuide, setShowGeneratedGuide] = useState(false);
  const [createdInvitationRow, setCreatedInvitationRow] = useState(null);
  const [regenerateTargetId, setRegenerateTargetId] = useState('');
  const [regenerateConfirmationInput, setRegenerateConfirmationInput] = useState('');
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [rsvpCloseTarget, setRsvpCloseTarget] = useState(null); // row to close/open RSVP
  const [rsvpCloseConfirmationInput, setRsvpCloseConfirmationInput] = useState('');

  /* ── Edit offcanvas ───────────────────────────────────── */
  const [editDrawerRow, setEditDrawerRow] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', rsvpEmail: '', eventDate: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editMessage, setEditMessage] = useState('');

  /* ── UI ───────────────────────────────────────────────── */
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ── Bookings ─────────────────────────────────────────── */
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingStatusUpdating, setBookingStatusUpdating] = useState(null);
  const [bookingActionLoading, setBookingActionLoading] = useState(null);
  const [bookingActionTarget, setBookingActionTarget] = useState(null);
  const [openStatusMenu, setOpenStatusMenu] = useState(null);
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [pushRegistered, setPushRegistered] = useState(false);
  const socketRef = useRef(null);

  /* ── Reviews ────────────────────────────────────────────────────── */
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewReplyDraft, setReviewReplyDraft] = useState({});
  const [reviewActionLoading, setReviewActionLoading] = useState(null);
  const [deleteReviewTarget, setDeleteReviewTarget] = useState(null);
  const [reviewSaveStatus, setReviewSaveStatus] = useState({}); // { [id]: { type: 'success'|'error', msg: string } }
  const [replyEmailConfirmId, setReplyEmailConfirmId] = useState(null); // id awaiting email-send confirmation

  /* ── Manual Review Modal ─────────────────────────────────────── */
  const [showManualReview, setShowManualReview] = useState(false);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [completedBookingsLoading, setCompletedBookingsLoading] = useState(false);
  const [manualReviewSubmitting, setManualReviewSubmitting] = useState(false);
  const [manualReviewError, setManualReviewError] = useState('');
  const [manualForm, setManualForm] = useState({
    bookingId: '', reviewerName: '', reviewerEmail: '',
    eventType: '', templateName: '', eventDate: '',
    rating: 0, ratingDesign: 0, ratingCommunication: 0, ratingValue: 0,
    comment: '',
  });
  const [manualImages, setManualImages] = useState([]); // File[]
  const [manualImagePreviews, setManualImagePreviews] = useState([]); // string[] (object URLs)

  const isLoggedIn = !!token;

  /* ── Derived invitation values ────────────────────────── */
  const selectedCategoryOption = useMemo(
    () => INVITATION_CATEGORY_OPTIONS.find((o) => o.value === newCategory) || INVITATION_CATEGORY_OPTIONS[0],
    [newCategory]
  );
  const pendingInvitationId = useMemo(() => normalizeInvitationId(newInvitationId), [newInvitationId]);
  const pendingDesignFilePath = useMemo(
    () => `e-inv-app/src/pages/invitations/designs/${toDesignFileName(newInvitationId)}`,
    [newInvitationId]
  );
  const pendingPreviewPath = useMemo(
    () => `/invitations/custom/${pendingInvitationId || '<invitation-id>'}`,
    [pendingInvitationId]
  );
  const liveGeneratedCode = useMemo(() => {
    if (!pendingInvitationId) return '';
    return buildClientGeneratedDesignSource(pendingInvitationId, newTitle.trim(), newCategory, newRsvpEmail.trim().toLowerCase());
  }, [pendingInvitationId, newTitle, newCategory, newRsvpEmail]);

  const baseGeneratedCode = generatedCode || liveGeneratedCode || '';
  let displayedCode = baseGeneratedCode;
  if (import.meta.env && import.meta.env.MODE === 'development') {
    const targetId = pendingInvitationId || newInvitationId || (createdInvitationRow && createdInvitationRow.invitationId) || '';
    if (targetId && !displayedCode.includes('/internal/dev/invitations')) {
      displayedCode += `

// Dev-only: auto-integrate when previewing locally
;(function(){
  if (import.meta && import.meta.env && import.meta.env.MODE !== 'development') return;
  try {
    const endpoint = (import.meta.env.VITE_API_BASE_URL || '') + '/internal/dev/invitations/' + encodeURIComponent(${JSON.stringify(targetId)}) + '/auto-integrate';
    fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ environment: 'local' }) }).catch(()=>{});
  } catch(e){}
})();
`;
    }
  }
  const displayedFileName = generatedFileName || toDesignFileName(newInvitationId);
  const displayedPreviewPath = generatedPreviewPath || pendingPreviewPath;

  const activeCreatedRow = useMemo(() => {
    const id = pendingInvitationId || newInvitationId;
    if (createdInvitationRow) return createdInvitationRow;
    if (!id) return null;
    return rows.find((r) => String(r.invitationId) === String(id)) || null;
  }, [createdInvitationRow, rows, pendingInvitationId, newInvitationId]);

  /* ── Dashboard stats (invitations) ───────────────────── */
  const dashboardStats = useMemo(() => {
    const totalInvitations = rows.length;
    const totalRsvps = rows.reduce((s, r) => s + (Number(r.totalRsvps) || 0), 0);
    const invitationsWithRsvps = rows.filter((r) => Number(r.totalRsvps) > 0).length;
    const invitationsWithoutRsvps = Math.max(totalInvitations - invitationsWithRsvps, 0);
    const invitationsWithCodes = rows.filter((r) => Boolean(String(r.accessCode || '').trim())).length;
    const categoryMap = rows.reduce((acc, r) => {
      const key = r.categoryLabel || r.category || 'Uncategorized';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topRows = [...rows].sort((a, b) => (Number(b.totalRsvps) || 0) - (Number(a.totalRsvps) || 0)).slice(0, 6);
    return {
      totalInvitations, totalRsvps, invitationsWithRsvps, invitationsWithoutRsvps,
      invitationsWithCodes,
      activeCategories: Object.keys(categoryMap).length,
      categoryLabels: Object.keys(categoryMap),
      categoryValues: Object.values(categoryMap),
      topInvitationLabels: topRows.map((r) => r.title || r.invitationId),
      topInvitationValues: topRows.map((r) => Number(r.totalRsvps) || 0),
      hasData: totalInvitations > 0
    };
  }, [rows]);

  /* ── Invitation chart data ────────────────────────────── */
  const categoryChartData = useMemo(() => ({
    labels: dashboardStats.categoryLabels,
    datasets: [{ data: dashboardStats.categoryValues, backgroundColor: DASHBOARD_PALETTE.slice(0, dashboardStats.categoryLabels.length), borderColor: '#fffaf3', borderWidth: 2, hoverOffset: 8 }]
  }), [dashboardStats.categoryLabels, dashboardStats.categoryValues]);

  const topInvitationsChartData = useMemo(() => ({
    labels: dashboardStats.topInvitationLabels,
    datasets: [{ label: 'RSVPs', data: dashboardStats.topInvitationValues, backgroundColor: '#7c2d12', borderRadius: 10, maxBarThickness: 42 }]
  }), [dashboardStats.topInvitationLabels, dashboardStats.topInvitationValues]);

  const responseSplitChartData = useMemo(() => ({
    labels: ['With RSVPs', 'No RSVPs yet'],
    datasets: [{ data: [dashboardStats.invitationsWithRsvps, dashboardStats.invitationsWithoutRsvps], backgroundColor: ['#0f766e', '#e7dccb'], borderColor: '#fffaf3', borderWidth: 2 }]
  }), [dashboardStats.invitationsWithRsvps, dashboardStats.invitationsWithoutRsvps]);

  /* ── Booking chart data ───────────────────────────────── */
  const bookingChartData = useMemo(() => {
    if (!bookingStats) return null;
    const byEventType = bookingStats.byEventType || [];
    const byMonth = bookingStats.byMonth || [];
    const byStatus = bookingStats.byStatus || [];
    const statusColors = { pending: '#b45309', in_progress: '#1d4ed8', completed: '#0f766e', cancelled: '#b91c1c' };
    return {
      eventTypeBar: {
        labels: byEventType.map((r) => capitalize(r.event_type || 'Other')),
        datasets: [{ label: 'Bookings', data: byEventType.map((r) => Number(r.cnt)), backgroundColor: DASHBOARD_PALETTE, borderRadius: 8, maxBarThickness: 40 }]
      },
      monthLine: {
        labels: byMonth.map((r) => r.month),
        datasets: [{ label: 'New Bookings', data: byMonth.map((r) => Number(r.cnt)), backgroundColor: 'rgba(124,45,18,0.15)', borderColor: '#7c2d12', borderWidth: 2, borderRadius: 6, maxBarThickness: 36 }]
      },
      statusDoughnut: {
        labels: byStatus.map((r) => capitalize(r.status.replace('_', ' '))),
        datasets: [{ data: byStatus.map((r) => Number(r.cnt)), backgroundColor: byStatus.map((r) => statusColors[r.status] || '#9a8a7e'), borderColor: '#fffaf3', borderWidth: 2, hoverOffset: 8 }]
      },
      sourceBar: {
        labels: (bookingStats.bySource || []).map((r) => r.source),
        datasets: [{ label: 'Bookings', data: (bookingStats.bySource || []).map((r) => Number(r.cnt)), backgroundColor: DASHBOARD_PALETTE, borderRadius: 8, maxBarThickness: 44 }]
      },
      sourceRows: bookingStats.bySource || []
    };
  }, [bookingStats]);

  const reviewComputedStats = useMemo(() => {
    const total = reviews.length;
    const published = reviews.filter(r => r.is_published).length;
    const hidden = total - published;
    const ratings = reviews.map(r => Number(r.rating)).filter(Boolean);
    const avgRating = ratings.length > 0 ? (ratings.reduce((s, v) => s + v, 0) / ratings.length).toFixed(1) : null;
    return { total, published, hidden, avgRating };
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    if (reviewFilter === 'published') return reviews.filter(r => r.is_published);
    if (reviewFilter === 'hidden') return reviews.filter(r => !r.is_published);
    return reviews;
  }, [reviews, reviewFilter]);
  const invitationBadgeCount = rows.length;
  const bookingPendingCount = Number(
    bookingStats?.totals?.pending ?? bookings.filter((booking) => booking.status === 'pending').length
  );
  const canCreateInvitationFromBooking = (booking) => (
    !['completed', 'cancelled'].includes(String(booking?.status || '').toLowerCase())
  );
  const getBookingStatus = (booking) => String(booking?.status || '').toLowerCase();
  const canDeleteBooking = (booking) => getBookingStatus(booking) === 'cancelled' || import.meta.env.DEV;

  function selectAdminTab(tab) {
    const nextTab = getAdminTab(tab) || 'invitations';

    localStorage.setItem(ADMIN_ACTIVE_TAB_KEY, nextTab);
    if (nextTab === 'bookings') localStorage.setItem(ADMIN_BOOKING_VIEW_KEY, bookingView);

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('tab', nextTab);
      if (nextTab === 'bookings') {
        nextParams.set('bookingView', bookingView);
      } else {
        nextParams.delete('bookingView');
      }
      return nextParams;
    });
  }

  function selectBookingView(view) {
    const nextView = getBookingView(view) || 'active';

    setExpandedBooking(null);
    localStorage.setItem(ADMIN_ACTIVE_TAB_KEY, 'bookings');
    localStorage.setItem(ADMIN_BOOKING_VIEW_KEY, nextView);

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('tab', 'bookings');
      nextParams.set('bookingView', nextView);
      return nextParams;
    });
  }

  /* ── Chart options ────────────────────────────────────── */
  const doughnutOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { color: CHART_TEXT_COLOR, boxWidth: 14, padding: 16, font: { family: 'Georgia, serif', size: 12 } } } }
  }), []);

  const barOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    scales: {
      x: { ticks: { color: CHART_TEXT_COLOR, font: { family: 'Georgia, serif', size: 12 } }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0, color: CHART_TEXT_COLOR, font: { family: 'Georgia, serif', size: 12 } }, grid: { color: CHART_GRID_COLOR } }
    },
    plugins: { legend: { display: false } }
  }), []);

  /* ── Session helpers ──────────────────────────────────── */
  function resetToLogin(message = 'Your admin session expired. Please sign in again.') {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.setItem(ADMIN_NOTICE_KEY, message);
    setToken(''); setRows([]); setPassword(''); setError(''); setResultMessage('');
    setAuthMessage(message);
  }

  async function readAdminResponse(res, fallbackMessage) {
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) { resetToLogin(); return null; }
    if (!res.ok) throw new Error(json.message || fallbackMessage);
    return json;
  }

  /* ── Invitation API ───────────────────────────────────── */
  async function fetchCodes(currentToken = token) {
    if (!currentToken) return;
    setLoading(true); setError(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(ADMIN_API_BASE_PATH), { headers: { Authorization: `Bearer ${currentToken}` } });
      const json = await readAdminResponse(res, 'Failed to load invitation records.');
      if (!json) return;
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (err) {
      setError(err.message || 'Failed to load invitation records.');
    } finally { setLoading(false); }
  }

  async function syncDesignInvitations() {
    setLoading(true); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/sync`), { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const json = await readAdminResponse(res, 'Failed to sync invitations from designs.');
      if (!json) return;
      setResultMessage(`Synced ${json.synced || 0} design invitation file(s).`);
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (err) {
      setError(err.message || 'Failed to sync invitations from designs.');
    } finally { setLoading(false); }
  }

  function openAddFormFromBooking(b) {
    if (!canCreateInvitationFromBooking(b)) {
      setResultMessage('This booking is already completed, so invitation creation is locked.');
      return;
    }

    // Normalize a raw string to a slug that can match category option values
    function toSlug(str) {
      return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    }
    function findCategory(raw) {
      if (!raw) return null;
      const slug = toSlug(raw);
      if (!slug) return null;
      return INVITATION_CATEGORY_OPTIONS.find((o) => o.value === slug)
        || INVITATION_CATEGORY_OPTIONS.find((o) => slug.startsWith(o.value))
        || INVITATION_CATEGORY_OPTIONS.find((o) => o.value.startsWith(slug))
        || null;
    }
    // Extract category from "Template Name (Category)" pattern
    function extractParenCategory(str) {
      const m = String(str || '').match(/\(([^)]+)\)\s*$/);
      return m ? m[1].trim() : null;
    }

    let matchedCategory;
    if (b.design_type === 'template') {
      // Priority: template_category column → category in selected_template parens
      // → category in template_name parens → event_type → first option
      matchedCategory =
        findCategory(b.template_category)
        || findCategory(extractParenCategory(b.selected_template))
        || findCategory(extractParenCategory(b.template_name))
        || findCategory(b.event_type)
        || INVITATION_CATEGORY_OPTIONS[0];
    } else {
      // For custom bookings: derive from event_type
      matchedCategory = findCategory(b.event_type) || INVITATION_CATEGORY_OPTIONS[0];
    }

    // Build suggested ID from celebrant name + event type
    const suggestedId = normalizeInvitationId(
      `${b.celebrant_name || b.full_name || ''}-${b.event_type || ''}`.replace(/\s+/g, '-')
    );
    setNewInvitationId(suggestedId);
    setNewCategory(matchedCategory.value);
    setNewTitle(b.template_name || (b.celebrant_name ? `${b.celebrant_name}'s ${b.event_type || 'Event'}` : ''));
    setNewRsvpEmail(b.rsvp_email || b.email || '');
    // Handle both 'YYYY-MM-DD' string and ISO datetime string from MySQL
    const rawDate = b.event_date
    const formattedDate = rawDate
      ? (/^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : new Date(rawDate).toLocaleDateString('sv-SE'))
      : ''
    setNewEventDate(formattedDate);
    setOffcanvasError('');
    setSourceBookingId(b.id);
    // Clear any previously generated code when opening the form
    setGeneratedCode(''); setGeneratedFileName(''); setGeneratedPreviewPath(''); setShowGeneratedGuide(false);
    setCreatedInvitationRow(null);
    setShowAddForm(true);
  }

  function openDeveloperGuideForInvitation(row) {
    if (!row || !row.invitationId) return;

    const rawDate = row.eventDate;
    const formattedDate = rawDate
      ? (/^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : new Date(rawDate).toLocaleDateString('sv-SE'))
      : '';

    // Pre-fill the add-invitation form so the live Developer Guide renders for this invitation
    setNewInvitationId(row.invitationId);
    setNewCategory(row.category || INVITATION_CATEGORY_OPTIONS[0].value);
    setNewTitle(row.title || '');
    setNewRsvpEmail(row.rsvpEmail || '');
    setNewEventDate(formattedDate);
    setOffcanvasError('');
    setSourceBookingId(null);
    // Clear any server-generated code so the live preview (client template) is used
    setGeneratedCode(''); setGeneratedFileName(''); setGeneratedPreviewPath(''); setShowGeneratedGuide(false);
    setCreatedInvitationRow(row);
    setShowAddForm(true);
  }

  async function addInvitation(e) {
    e.preventDefault();
    const invitationIdValue = newInvitationId.trim();
    const categoryValue = String(newCategory || '').trim();
    const rsvpEmailValue = newRsvpEmail.trim().toLowerCase();
    if (!invitationIdValue || !categoryValue || !rsvpEmailValue) {
      setError('Invitation ID, category, and RSVP email are required.'); return;
    }
    if (!newEventDate) {
      setOffcanvasError('Event date is required.'); return;
    }
    setLoading(true); setOffcanvasError(''); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(ADMIN_API_BASE_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invitationId: invitationIdValue, category: categoryValue, title: newTitle.trim(), rsvpEmail: rsvpEmailValue, eventDate: newEventDate || null })
      });
      const json = await readAdminResponse(res, 'Failed to create invitation.');
      if (!json) return;
      setResultMessage(`Created invitation: ${json.row?.invitationId || invitationIdValue}`);
      const createdInvitationId = json.row?.invitationId || invitationIdValue;
      // Capture generated source for developer copy/paste (server may not write files in production)
      const generated = String(json.row?.generatedSource || '');
      const compFile = String(json.row?.componentFileName || toDesignFileName(createdInvitationId));
      const preview = String(json.row?.previewPath || `/invitations/custom/${normalizeInvitationId(createdInvitationId)}`);
      setGeneratedCode(generated);
      setGeneratedFileName(compFile);
      setGeneratedPreviewPath(preview);
      // Show the generated copy/paste guide whenever the server returned generated code.
      setGeneratedFileCreated(Boolean(json.row?.fileCreated));
      setShowGeneratedGuide(Boolean(generated));
      // Remember created DB row so the offcanvas can show integration status
      setCreatedInvitationRow(json.row || { invitationId: createdInvitationId, integrationStatus: (json.row && (json.row.integrationStatus || json.row.integration_status)) || 'awaiting_setup' });
      // Link the source booking to this invitation if opened from a booking
      if (sourceBookingId) {
        try {
          const linkRes = await fetch(getApi(`/bookings/${sourceBookingId}/link-invitation`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ invitationId: createdInvitationId })
          });
          if (linkRes.ok) {
            setBookings((prev) => prev.map((b) =>
              b.id === sourceBookingId ? { ...b, linked_invitation_id: createdInvitationId } : b
            ));
          }
        } catch { /* non-blocking */ }
        setSourceBookingId(null);
      }
      // Keep the add form open so the Developer Guide and integration status are visible to admins.
      // (Previously closed when no generated code was returned.)
      await fetchCodes(token);
    } catch (err) {
      setOffcanvasError(err.message || 'Failed to create invitation.');
    } finally { setLoading(false); }
  }

  /* ── Auth ─────────────────────────────────────────────── */
  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError(''); setAuthMessage('');
    try {
      const res = await fetch(getApi('/admin/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Login failed.');
      localStorage.setItem(ADMIN_TOKEN_KEY, json.token);
      sessionStorage.removeItem(ADMIN_NOTICE_KEY);
      setToken(json.token); setPassword('');
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally { setLoading(false); }
  }

  async function handleLogout() {
    try { await fetch(getApi('/admin/logout'), { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch { /* noop */ }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_NOTICE_KEY);
    setToken(''); setRows([]); setAuthMessage('');
  }

  /* ── Code management ──────────────────────────────────── */
  async function ensureCode(e) {
    e.preventDefault();
    if (!invitationId.trim()) return;
    setLoading(true); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/codes/ensure`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invitationId: invitationId.trim() })
      });
      const json = await readAdminResponse(res, 'Failed to ensure code.');
      if (!json) return;
      setResultMessage(`Code for ${json.invitationId}: ${json.accessCode}`);
      await fetchCodes(token);
    } catch (err) {
      setError(err.message || 'Failed to ensure code.');
    } finally { setLoading(false); }
  }

  async function copyGeneratedCode() {
    if (!generatedCode) return;
    try {
      await navigator.clipboard.writeText(generatedCode);
      setToast({ type: 'success', msg: 'Code copied to clipboard' });
    } catch (err) {
      const el = document.getElementById('adm-generated-code-textarea');
      if (el) {
        try {
          el.select();
          document.execCommand('copy');
          setToast({ type: 'success', msg: 'Code copied to clipboard' });
        } catch (e) {
          setToast({ type: 'error', msg: 'Copy failed' });
        }
        window.getSelection().removeAllRanges();
      } else {
        setToast({ type: 'error', msg: 'Copy failed' });
      }
    }
  }

  async function copyToClipboard(text, successMsg = 'Copied to clipboard') {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setToast({ type: 'success', msg: successMsg });
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setToast({ type: 'success', msg: successMsg });
      } catch (e) {
        setToast({ type: 'error', msg: 'Copy failed' });
      }
    }
  }

  function openRegenerateConfirmation(id) {
    setRegenerateTargetId(id);
    setRegenerateConfirmationInput('');
    setError(''); setResultMessage(''); setAuthMessage('');
  }

  function closeRegenerateConfirmation() {
    if (loading) return;
    setRegenerateTargetId(''); setRegenerateConfirmationInput('');
  }

  function openDeleteConfirmation(row) {
    if (!row?.canDelete) return;
    setDeleteTargetRow(row); setDeleteConfirmationInput('');
    setError(''); setResultMessage(''); setAuthMessage('');
  }

  function closeDeleteConfirmation() {
    if (loading) return;
    setDeleteTargetRow(null); setDeleteConfirmationInput('');
  }

  async function regenerateCode(id) {
    const normalizedId = String(id || '').trim();
    if (!normalizedId) return false;
    setLoading(true); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/codes/${encodeURIComponent(normalizedId)}/regenerate`), {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const json = await readAdminResponse(res, 'Failed to regenerate code.');
      if (!json) return false;
      setResultMessage(`New code for ${json.invitationId}: ${json.accessCode}`);
      await fetchCodes(token);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to regenerate code.');
      return false;
    } finally { setLoading(false); }
  }

  async function confirmRegenerateCode(e) {
    e.preventDefault();
    if (!regenerateTargetId) return;
    if (regenerateConfirmationInput.trim() !== regenerateTargetId) {
      setError(`Type "${regenerateTargetId}" exactly to confirm.`); setResultMessage(''); setAuthMessage(''); return;
    }
    const success = await regenerateCode(regenerateTargetId);
    if (success) closeRegenerateConfirmation();
  }

  async function deleteInvitationRow(id) {
    const normalizedId = String(id || '').trim();
    if (!normalizedId) return false;
    setLoading(true); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/${encodeURIComponent(normalizedId)}`), {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      const json = await readAdminResponse(res, 'Failed to delete invitation.');
      if (!json) return false;
      const parts = [
        `Deleted invitation ${json.invitationId}.`,
        json.fileDeleted === false && json.sourcePath ? `Database record removed, source file needs manual review: ${json.sourcePath}.` : ''
      ].filter(Boolean);
      setResultMessage(parts.join(' '));
      await fetchCodes(token);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete invitation.');
      return false;
    } finally { setLoading(false); }
  }

  async function confirmDeleteInvitation(e) {
    e.preventDefault();
    if (!deleteTargetRow?.invitationId) return;
    if (deleteConfirmationInput.trim() !== deleteTargetRow.invitationId) {
      setError(`Type "${deleteTargetRow.invitationId}" exactly to confirm.`); setResultMessage(''); setAuthMessage(''); return;
    }
    const success = await deleteInvitationRow(deleteTargetRow.invitationId);
    if (success) closeDeleteConfirmation();
  }

  /* ── RSVP Close toggle ────────────────────────────────── */
  function isRowRsvpEffectivelyClosed(row) {
    if (row.rsvpClosed) return true;
    if (row.eventDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today > new Date(row.eventDate);
    }
    return false;
  }

  async function toggleRsvpClosed(row, close) {
    setLoading(true); setError(''); setResultMessage(''); setAuthMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/${encodeURIComponent(row.invitationId)}/rsvp-status`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ closed: close })
      });
      const json = await readAdminResponse(res, 'Failed to update RSVP status.');
      if (!json) return;
      setResultMessage(close ? `RSVP closed for "${row.invitationId}".` : `RSVP re-opened for "${row.invitationId}".`);
      await fetchCodes(token);
    } catch (err) {
      setError(err.message || 'Failed to update RSVP status.');
    } finally {
      setLoading(false);
      setRsvpCloseTarget(null);
      setRsvpCloseConfirmationInput('');
    }
  }

  /* ── Edit offcanvas handlers ──────────────────────────── */
  function openEditDrawer(row) {
    const rawDate = row.eventDate;
    const formattedDate = rawDate
      ? (/^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : new Date(rawDate).toLocaleDateString('sv-SE'))
      : '';
    setEditForm({ title: row.title || '', rsvpEmail: row.rsvpEmail || '', eventDate: formattedDate });
    setEditError('');
    setEditMessage('');
    setEditDrawerRow(row);
  }

  function closeEditDrawer() {
    if (editSaving) return;
    setEditDrawerRow(null);
    setEditError('');
    setEditMessage('');
  }

  async function saveEditDrawer(e) {
    e.preventDefault();
    if (!editDrawerRow) return;
    setEditSaving(true); setEditError(''); setEditMessage('');
    try {
      const res = await fetch(getApi(`${ADMIN_API_BASE_PATH}/${encodeURIComponent(editDrawerRow.invitationId)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: editForm.title.trim(),
          rsvpEmail: editForm.rsvpEmail.trim().toLowerCase(),
          eventDate: editForm.eventDate || null
        })
      });
      const json = await readAdminResponse(res, 'Failed to save invitation.');
      if (!json) return;
      setEditMessage('Saved successfully.');
      setRows((prev) => prev.map((r) => r.invitationId === editDrawerRow.invitationId ? { ...r, title: editForm.title.trim(), rsvpEmail: editForm.rsvpEmail.trim().toLowerCase(), eventDate: editForm.eventDate || null } : r));
    } catch (err) {
      setEditError(err.message || 'Failed to save invitation.');
    } finally {
      setEditSaving(false);
    }
  }

  // ESC key closes the drawer
  React.useEffect(() => {
    if (!editDrawerRow) return;
    function onKeyDown(e) {
      if (e.key === 'Escape' && !editSaving) {
        setEditDrawerRow(null); setEditError(''); setEditMessage('');
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editDrawerRow, editSaving]);

  /* ── Bookings API ─────────────────────────────────────── */
  async function fetchBookings(currentToken = token, view = bookingView) {
    if (!currentToken) return;
    setBookingsLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        fetch(getApi(`/bookings?view=${encodeURIComponent(view)}`), { headers: { Authorization: `Bearer ${currentToken}` } }),
        fetch(getApi('/bookings/stats'), { headers: { Authorization: `Bearer ${currentToken}` } })
      ]);
      const listJson = await listRes.json().catch(() => ({}));
      const statsJson = await statsRes.json().catch(() => ({}));
      if (listRes.status === 401 || statsRes.status === 401) { resetToLogin(); return; }
      setBookings(Array.isArray(listJson.bookings) ? listJson.bookings : []);
      if (statsJson.ok) setBookingStats(statsJson);
    } catch (err) {
      setError(err.message || 'Failed to load bookings.');
    } finally { setBookingsLoading(false); }
  }

  async function fetchBookingStats(currentToken = token) {
    if (!currentToken) return;
    try {
      const res = await fetch(getApi('/bookings/stats'), {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) { resetToLogin(); return; }
      if (json.ok) setBookingStats(json);
    } catch (err) {
      setError(err.message || 'Failed to load booking notifications.');
    }
  }

  function requestBookingStatusChange(booking, status) {
    setOpenStatusMenu(null);
    if (booking.status === status) return;

    setStatusChangeTarget({
      id: booking.id,
      clientName: booking.full_name,
      eventType: booking.event_type,
      currentStatus: booking.status,
      nextStatus: status
    });
  }

  async function confirmBookingStatusChange() {
    if (!statusChangeTarget) return;
    const { id, nextStatus } = statusChangeTarget;
    setStatusChangeTarget(null);
    await changeBookingStatus(id, nextStatus);
  }

  async function changeBookingStatus(id, status) {
    const previousBooking = bookings.find((booking) => booking.id === id);
    if (previousBooking?.status === status) {
      setOpenStatusMenu(null);
      return;
    }

    const statusLabel = getBookingStatusOption(status).label;
    setBookingStatusUpdating(id);
    setOpenStatusMenu(null);
    setBookings((prev) => prev.map((booking) => booking.id === id ? { ...booking, status } : booking));
    try {
      const res = await fetch(getApi(`/bookings/${id}/status`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) { resetToLogin(); return; }
      if (!res.ok) throw new Error(json.message || 'Failed to update status.');
      await fetchBookings(token, bookingView);
      setResultMessage(`Booking #${id} marked as ${statusLabel}.`);
    } catch (err) {
      if (previousBooking) {
        setBookings((prev) => prev.map((booking) => booking.id === id ? previousBooking : booking));
      }
      setError(err.message || 'Failed to update status.');
    } finally { setBookingStatusUpdating(null); }
  }

  /* ── Push ─────────────────────────────────────────────── */
  function requestBookingAction(type, booking) {
    setBookingActionTarget({ type, booking });
    setError('');
    setResultMessage('');
  }

  function closeBookingActionConfirmation() {
    if (bookingActionLoading) return;
    setBookingActionTarget(null);
  }

  async function confirmBookingAction() {
    if (!bookingActionTarget?.booking?.id) return;

    const { type, booking } = bookingActionTarget;
    const actionKey = `${type}-${booking.id}`;
    setBookingActionLoading(actionKey);
    setError('');
    setResultMessage('');

    try {
      if (type === 'cancel') {
        setBookingActionTarget(null);
        await changeBookingStatus(booking.id, 'cancelled');
        return;
      }

      if (type === 'restore') {
        setBookingActionTarget(null);
        await changeBookingStatus(booking.id, 'pending');
        return;
      }

      const endpointByType = {
        archive: { method: 'PATCH', path: `/bookings/${booking.id}/archive`, message: `Booking #${booking.id} archived.` },
        unarchive: { method: 'PATCH', path: `/bookings/${booking.id}/unarchive`, message: `Booking #${booking.id} restored to active.` },
        delete: { method: 'DELETE', path: `/bookings/${booking.id}`, message: `Booking #${booking.id} deleted.` },
        resendReview: { method: 'POST', path: `/bookings/${booking.id}/review-request`, message: `Review request resent for booking #${booking.id}.` }
      };
      const action = endpointByType[type];
      if (!action) return;

      const res = await fetch(getApi(action.path), {
        method: action.method,
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await readAdminResponse(res, `Failed to ${type} booking.`);
      if (!json) return;

      if (['archive', 'unarchive', 'delete'].includes(type)) {
        setBookings((prev) => prev.filter((item) => item.id !== booking.id));
        setExpandedBooking((current) => current === booking.id ? null : current);
        // Optimistically update stats counts so the UI reflects the change immediately
        setBookingStats((prev) => {
          if (!prev) return prev;
          const totals = { ...(prev.totals || {}) };
          const st = String(booking.status || '').toLowerCase();
          if (type === 'delete') {
            totals.total = Math.max(0, (Number(totals.total) || 0) - 1);
            totals[st] = Math.max(0, (Number(totals[st]) || 0) - 1);
            if (booking.design_type === 'template') totals.template_count = Math.max(0, (Number(totals.template_count) || 0) - 1);
            if (booking.design_type === 'custom') totals.custom_count = Math.max(0, (Number(totals.custom_count) || 0) - 1);
          } else if (type === 'archive') {
            totals.total = Math.max(0, (Number(totals.total) || 0) - 1);
            totals.archived = (Number(totals.archived) || 0) + 1;
          } else if (type === 'unarchive') {
            totals.total = (Number(totals.total) || 0) + 1;
            totals.archived = Math.max(0, (Number(totals.archived) || 0) - 1);
          }
          return { ...prev, totals };
        });
        fetchBookingStats(token); // background refresh to sync with server
      }

      setResultMessage(json.message || action.message);
      setBookingActionTarget(null);
    } catch (err) {
      setError(err.message || 'Booking action failed.');
    } finally {
      setBookingActionLoading(null);
    }
  }

  async function fetchReviews(ct = token) {
    if (!ct) return;
    setReviewsLoading(true);
    try {
      const res = await fetch(getApi('/reviews'), { headers: { Authorization: `Bearer ${ct}` } });
      const json = await readAdminResponse(res, 'Failed to load reviews.');
      if (!json) return;
      setReviews(Array.isArray(json.reviews) ? json.reviews : []);
      setReviewReplyDraft(prev => { const next = { ...prev }; (json.reviews || []).forEach(r => { next[r.id] = r.admin_reply || ''; }); return next; });
    } catch (err) { setError(err.message || 'Failed to load reviews.'); }
    finally { setReviewsLoading(false); }
  }

  async function fetchCompletedBookings() {
    setCompletedBookingsLoading(true);
    try {
      const res = await fetch(getApi('/reviews/completed-bookings'), { headers: { Authorization: `Bearer ${token}` } });
      const json = await readAdminResponse(res, 'Failed to load completed bookings.');
      if (!json) return;
      setCompletedBookings(Array.isArray(json.bookings) ? json.bookings : []);
    } catch (err) { setManualReviewError(err.message || 'Failed to load completed bookings.'); }
    finally { setCompletedBookingsLoading(false); }
  }

  function openManualReviewModal() {
    setManualForm({ bookingId: '', reviewerName: '', reviewerEmail: '', eventType: '', templateName: '', eventDate: '', rating: 0, ratingDesign: 0, ratingCommunication: 0, ratingValue: 0, comment: '' });
    setManualImages([]);
    setManualImagePreviews([]);
    setManualReviewError('');
    setShowManualReview(true);
    fetchCompletedBookings();
  }

  function closeManualReviewModal() {
    manualImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setManualImagePreviews([]);
    setManualImages([]);
    setShowManualReview(false);
  }

  function handleManualBookingSelect(bookingId) {
    const bk = completedBookings.find(b => String(b.id) === String(bookingId));
    if (!bk) { setManualForm(p => ({ ...p, bookingId })); return; }
    setManualForm(p => ({
      ...p,
      bookingId: String(bk.id),
      reviewerName: bk.full_name || '',
      reviewerEmail: bk.email || '',
      eventType: bk.event_type || '',
      templateName: bk.template_name || '',
      eventDate: bk.event_date ? bk.event_date.slice(0, 10) : '',
    }));
  }

  function handleManualImageChange(e) {
    const files = Array.from(e.target.files || []);
    const combined = [...manualImages, ...files].slice(0, 3);
    manualImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setManualImages(combined);
    setManualImagePreviews(combined.map(f => URL.createObjectURL(f)));
    e.target.value = '';
  }

  function removeManualImage(idx) {
    URL.revokeObjectURL(manualImagePreviews[idx]);
    setManualImages(prev => prev.filter((_, i) => i !== idx));
    setManualImagePreviews(prev => prev.filter((_, i) => i !== idx));
  }

  async function submitManualReview(e) {
    e.preventDefault();
    setManualReviewError('');
    if (!manualForm.bookingId) { setManualReviewError('Please select a completed booking.'); return; }
    if (!manualForm.rating) { setManualReviewError('Overall rating is required.'); return; }
    setManualReviewSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(manualForm).forEach(([k, v]) => fd.append(k, v));
      manualImages.forEach(f => fd.append('images', f));
      const res = await fetch(getApi('/reviews/manual'), { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const json = await readAdminResponse(res, 'Failed to create review.');
      if (!json) return;
      setResultMessage('Manual review created and published.');
      closeManualReviewModal();
      fetchReviews(token);
    } catch (err) { setManualReviewError(err.message || 'Failed to create review.'); }
    finally { setManualReviewSubmitting(false); }
  }

  async function togglePublishReview(id, publish) {
    setReviewActionLoading(id);
    try {
      const res = await fetch(getApi(`/reviews/${id}/publish`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ publish }) });
      const json = await readAdminResponse(res, 'Failed to update review.');
      if (!json) return;
      setReviews(prev => prev.map(r => r.id === id ? { ...r, is_published: publish ? 1 : 0 } : r));
      setResultMessage(publish ? 'Review published.' : 'Review hidden.');
    } catch (err) { setError(err.message || 'Failed to update review.'); }
    finally { setReviewActionLoading(null); }
  }

  async function saveReviewReply(id) {
    setReviewActionLoading(`reply-${id}`);
    setReviewSaveStatus(prev => { const next = { ...prev }; delete next[id]; return next; });
    const reply = (reviewReplyDraft[id] ?? '').trim();
    try {
      const res = await fetch(getApi(`/reviews/${id}/reply`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ reply }) });
      const json = await readAdminResponse(res, 'Failed to save reply.');
      if (!json) return;
      setReviews(prev => prev.map(r => r.id === id ? { ...r, admin_reply: reply || null } : r));
      setReviewReplyDraft(prev => ({ ...prev, [id]: reply }));
      setReviewSaveStatus(prev => ({ ...prev, [id]: { type: 'success', msg: reply ? 'Reply saved.' : 'Reply cleared.' } }));
    } catch (err) {
      setReviewSaveStatus(prev => ({ ...prev, [id]: { type: 'error', msg: err.message || 'Failed to save reply.' } }));
    }
    finally { setReviewActionLoading(null); }
  }

  async function sendReviewReplyEmail(id) {
    setReviewActionLoading(`reply-email-${id}`);
    setReviewSaveStatus(prev => { const next = { ...prev }; delete next[id]; return next; });
    const reply = (reviewReplyDraft[id] ?? '').trim();
    if (!reply) {
      setError('Write a reply before sending an email.');
      setReviewActionLoading(null);
      return;
    }

    try {
      const res = await fetch(getApi(`/reviews/${id}/reply/email`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reply })
      });
      const json = await readAdminResponse(res, 'Failed to send reply email.');
      if (!json) return;
      setReviews(prev => prev.map(r => r.id === id ? { ...r, admin_reply: reply || null } : r));
      setReviewReplyDraft(prev => ({ ...prev, [id]: reply }));
      setReviewSaveStatus(prev => ({ ...prev, [id]: { type: 'success', msg: json.message || 'Reply saved and emailed to the customer.' } }));
    } catch (err) {
      setReviewSaveStatus(prev => ({ ...prev, [id]: { type: 'error', msg: err.message || 'Failed to send reply email.' } }));
    }
    finally { setReviewActionLoading(null); }
  }

  async function confirmDeleteReview() {
    if (!deleteReviewTarget) return;
    setReviewActionLoading(deleteReviewTarget);
    try {
      const res = await fetch(getApi(`/reviews/${deleteReviewTarget}`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const json = await readAdminResponse(res, 'Failed to delete review.');
      if (!json) return;
      setReviews(prev => prev.filter(r => r.id !== deleteReviewTarget));
      setDeleteReviewTarget(null);
      setResultMessage('Review deleted.');
    } catch (err) { setError(err.message || 'Failed to delete review.'); }
    finally { setReviewActionLoading(null); }
  }

  async function enablePushNotifications() {
    const result = await registerAdminPush(token);
    if (result.ok) {
      setPushRegistered(true);
      setResultMessage('Push notifications enabled for this browser.');
    } else {
      const messages = {
        'https-required': 'Push notifications require HTTPS. This site is on HTTP — set up SSL/TLS to enable push alerts.',
        'unsupported': 'Your browser does not support push notifications.',
        'no-vapid-key': 'Server VAPID key not found. Check server configuration.',
        'permission-denied': 'Notification permission is blocked. Open your browser site settings, allow notifications for this site, then try again.',
        'permission-dismissed': 'Notification permission was dismissed. Click "Enable Push" again and select Allow.'
      };
      setError(messages[result.reason] || `Push setup failed: ${result.message || result.reason}`);
    }
  }

  /* ── Seat checker helpers ─────────────────────────────── */
  const checkerBase = useMemo(() => '/seat-checker', []);

  function getCheckerLink(code) {
    const c = String(code || '').trim().toUpperCase();
    return c ? `${checkerBase}?code=${encodeURIComponent(c)}` : checkerBase;
  }

  function prepareAdminCheckerLink(code) {
    const c = String(code || '').trim().toUpperCase();
    if (!c) return;
    localStorage.setItem(ADMIN_CHECKER_DEEPLINK_KEY, JSON.stringify({ code: c, expiresAt: Date.now() + 60_000 }));
  }

  /* ── Effects ──────────────────────────────────────────── */
  useEffect(() => {
    localStorage.setItem(ADMIN_ACTIVE_TAB_KEY, activeTab);
    localStorage.setItem(ADMIN_BOOKING_VIEW_KEY, bookingView);

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      let changed = false;

      if (nextParams.get('tab') !== activeTab) {
        nextParams.set('tab', activeTab);
        changed = true;
      }

      if (activeTab === 'bookings') {
        if (nextParams.get('bookingView') !== bookingView) {
          nextParams.set('bookingView', bookingView);
          changed = true;
        }
      } else if (nextParams.has('bookingView')) {
        nextParams.delete('bookingView');
        changed = true;
      }

      return changed ? nextParams : currentParams;
    }, { replace: true });
  }, [activeTab, bookingView, setSearchParams]);

  useEffect(() => { if (token) fetchCodes(token); }, [token]);
  useEffect(() => { if (token) fetchBookingStats(token); }, [token]);
  useEffect(() => { if (token && activeTab === 'bookings') fetchBookings(token, bookingView); }, [token, activeTab, bookingView]);
  useEffect(() => { if (token && activeTab === 'reviews') fetchReviews(token); }, [token, activeTab]);

  /* ── Toast auto-show whenever resultMessage / error / authMessage changes ── */
  function showToast(type, msg) {
    if (!msg) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, msg });
    toastTimerRef.current = setTimeout(() => setToast(null), 5000);
  }
  useEffect(() => { showToast('success', resultMessage); }, [resultMessage]); // eslint-disable-line
  useEffect(() => { showToast('error', error); }, [error]);         // eslint-disable-line
  useEffect(() => { showToast('info', authMessage); }, [authMessage]);   // eslint-disable-line

  useEffect(() => {
    if (openStatusMenu === null) return undefined;

    const closeStatusMenu = () => setOpenStatusMenu(null);
    const handleStatusMenuKeydown = (event) => {
      if (event.key === 'Escape') setOpenStatusMenu(null);
    };

    document.addEventListener('pointerdown', closeStatusMenu);
    document.addEventListener('keydown', handleStatusMenuKeydown);
    return () => {
      document.removeEventListener('pointerdown', closeStatusMenu);
      document.removeEventListener('keydown', handleStatusMenuKeydown);
    };
  }, [openStatusMenu]);

  // Restore push-enabled state on reload by checking for an existing browser subscription
  // Also re-syncs the subscription to the server (fixes silent failures when token was expired)
  useEffect(() => {
    if (!isPushAvailable() || !token) return;
    navigator.serviceWorker.getRegistration('/').then(reg => {
      if (!reg) return;
      reg.pushManager.getSubscription().then(async (sub) => {
        if (!sub) return;
        setPushRegistered(true);
        // Re-save to server on every page load — idempotent, ensures DB is in sync
        try {
          await fetch(getApi('/bookings/push/subscribe'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(sub.toJSON())
          });
        } catch (_) {
          // Silent best-effort — UI push state reflects browser, not server
        }
      });
    });
  }, [token]);

  // Real-time bookings via Socket.IO
  useEffect(() => {
    if (!token) return;

    const socketTarget = SOCKET_URL || window.location.origin;
    const socket = io(socketTarget, { transports: ['websocket', 'polling'], reconnectionDelay: 2000 });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[socket] Connected:', socket.id, '→ joining admin room');
      socket.emit('admin:join', token);
    });

    socket.on('connect_error', (err) => {
      console.warn('[socket] Connection error:', err.message);
    });

    socket.on('booking:new', (booking) => {
      // Prepend the new booking row
      setBookings((prev) => {
        if (prev.some((b) => b.id === booking.id)) return prev;
        return [booking, ...prev];
      });
      // Increment pending count in stats without a full re-fetch
      setBookingStats((prev) => {
        if (!prev) return prev;
        const totals = { ...(prev.totals || {}) };
        totals.total = Number(totals.total || 0) + 1;
        totals.pending = Number(totals.pending || 0) + 1;
        if (booking.design_type === 'template') totals.template_count = Number(totals.template_count || 0) + 1;
        if (booking.design_type === 'custom') totals.custom_count = Number(totals.custom_count || 0) + 1;
        return { ...prev, totals };
      });
    });

    socket.on('invitation:updated', (payload) => {
      try {
        const updated = payload?.row || payload
        if (!updated || !updated.invitationId) return

        setRows((prev) => {
          const exists = prev.some((r) => r.invitationId === updated.invitationId)
          if (exists) {
            return prev.map((r) => (r.invitationId === updated.invitationId ? { ...r, ...updated } : r))
          }
          // Prepend new invitation rows to the top
          return [updated, ...prev]
        })

        // If the offcanvas is showing a created invitation, update it too
        setCreatedInvitationRow((prev) => {
          if (!prev) return prev;
          if (String(prev.invitationId) === String(updated.invitationId)) return { ...prev, ...updated };
          return prev;
        })

        setToast({ type: 'success', msg: `Invitation ${updated.invitationId} updated` })
      } catch (e) {
        console.warn('Failed to process invitation:updated socket event', e)
      }
    })

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  function closeSidebar() { setSidebarOpen(false); }

  /* ══════════════════════════════════════════════════════════
     LOGIN SCREEN
  ══════════════════════════════════════════════════════════ */
  if (!isLoggedIn) {
    return (
      <div className="adm-root adm-login-page">
        <div className="adm-login-card">
          <div className="adm-login-logo">
            <div className="adm-login-logo-icon">✉</div>
            <div className="adm-login-logo-text">
              E-Invitation
              <span className="adm-login-logo-sub">Admin Console</span>
            </div>
          </div>
          <h1 className="adm-login-title">Sign in</h1>
          <p className="adm-login-subtitle">Access the admin dashboard to manage invitations and access codes.</p>
          <form onSubmit={handleLogin} style={{ display: 'grid', gap: '12px' }}>
            <div className="adm-field">
              <label className="adm-label" htmlFor="adm-username">Username</label>
              <input id="adm-username" className="adm-input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Admin username" autoComplete="username" required />
            </div>
            <div className="adm-field">
              <label className="adm-label" htmlFor="adm-password">Password</label>
              <input id="adm-password" className="adm-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" autoComplete="current-password" required />
            </div>
            {authMessage && <div className="adm-alert adm-alert-info">{authMessage}</div>}
            {error && <div className="adm-alert adm-alert-error">{error}</div>}
            <button type="submit" disabled={loading} className="adm-btn adm-btn-primary" style={{ marginTop: '4px' }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     DASHBOARD SHELL
  ══════════════════════════════════════════════════════════ */
  const bookingActionCopy = {
    cancel: {
      title: 'Cancel Booking',
      body: 'This moves the booking to Cancelled. You can restore it later.',
      confirm: 'Cancel Booking',
      danger: true
    },
    restore: {
      title: 'Restore Booking',
      body: 'This reopens the booking and moves it back to Pending.',
      confirm: 'Restore Booking'
    },
    archive: {
      title: 'Archive Booking',
      body: 'This hides the booking from the active list while keeping it in the database.',
      confirm: 'Archive Booking'
    },
    unarchive: {
      title: 'Restore Archived Booking',
      body: 'This moves the booking back to the active booking list.',
      confirm: 'Restore to Active'
    },
    delete: {
      title: 'Delete Booking',
      body: 'This permanently deletes the booking and related review request records. This cannot be undone.',
      confirm: 'Delete Permanently',
      danger: true
    },
    resendReview: {
      title: 'Resend Review Request',
      body: 'This sends the customer another review email for this completed booking.',
      confirm: 'Resend Email'
    }
  }[bookingActionTarget?.type] || null;

  return (
    <div className="adm-root adm-shell">

      {sidebarOpen && <div className="adm-drawer-overlay" onClick={closeSidebar} />}
      <aside className={`adm-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="adm-sidebar-header">
          <div className="adm-sidebar-brand">
            <div className="adm-sidebar-brand-icon">✉</div>
            <div>
              <div className="adm-sidebar-brand-name">E-Invitation</div>
              <div className="adm-sidebar-brand-sub">Admin Console</div>
            </div>
          </div>
        </div>

        <div className="adm-sidebar-nav">
          <div className="adm-sidebar-label">Dashboard</div>
          <button type="button" className={`adm-sidebar-btn${activeTab === 'invitations' ? ' active' : ''}`} onClick={() => { selectAdminTab('invitations'); closeSidebar(); }}>
            <span className="adm-sidebar-icon">▦</span> Invitations
            {invitationBadgeCount > 0 && (
              <span className="adm-sidebar-badge">{invitationBadgeCount}</span>
            )}
          </button>
          <button type="button" className={`adm-sidebar-btn${activeTab === 'bookings' ? ' active' : ''}`} onClick={() => { selectAdminTab('bookings'); closeSidebar(); }}>
            <span className="adm-sidebar-icon">📋</span> Bookings
            {bookingPendingCount > 0 && (
              <span className="adm-sidebar-badge adm-sidebar-badge--alert">
                {bookingPendingCount}
              </span>
            )}
          </button>

          <button type="button" className={`adm-sidebar-btn${activeTab === 'reviews' ? ' active' : ''}`} onClick={() => { selectAdminTab('reviews'); closeSidebar(); }}>
            <span className="adm-sidebar-icon">⭐</span> Reviews
            {reviewComputedStats.hidden > 0 && (
              <span className="adm-sidebar-badge adm-sidebar-badge--alert">
                {reviewComputedStats.hidden}
              </span>
            )}
          </button>

          <div className="adm-sidebar-label" style={{ marginTop: '8px' }}>Actions</div>
          {activeTab === 'invitations' && (<>
            <button type="button" className="adm-sidebar-btn" onClick={() => { fetchCodes(token); closeSidebar(); }} disabled={loading}>
              <span className="adm-sidebar-icon">↻</span> Refresh Data
            </button>
            <button type="button" className="adm-sidebar-btn" onClick={() => { syncDesignInvitations(); closeSidebar(); }} disabled={loading}>
              <span className="adm-sidebar-icon">⟳</span> Sync Designs
            </button>
            <button type="button" className="adm-sidebar-btn" onClick={() => { setCreatedInvitationRow(null); setShowAddForm((v) => !v); closeSidebar(); }} disabled={loading}>
              <span className="adm-sidebar-icon">＋</span> {showAddForm ? 'Cancel Add' : 'Add Invitation'}
            </button>
          </>)}
          {activeTab === 'bookings' && (<>
            <button type="button" className="adm-sidebar-btn" onClick={() => { fetchBookings(token, bookingView); closeSidebar(); }} disabled={bookingsLoading}>
              <span className="adm-sidebar-icon">↻</span> Refresh Bookings
            </button>
            {isPushAvailable() ? (
              <button type="button" className={`adm-sidebar-btn${pushRegistered ? ' active' : ''}`} onClick={() => { enablePushNotifications(); closeSidebar(); }}>
                <span className="adm-sidebar-icon">🔔</span> {pushRegistered ? 'Push Enabled ✓' : 'Enable Push Alerts'}
              </button>
            ) : (
              <div className="adm-sidebar-btn" style={{ opacity: 0.5, cursor: 'default', fontSize: '11px' }} title="Push notifications require HTTPS">
                <span className="adm-sidebar-icon">🔔</span> Push (HTTPS only)
              </div>
            )}
          </>)}
          {activeTab === 'reviews' && (<>
            <button type="button" className="adm-sidebar-btn" onClick={() => { fetchReviews(token); closeSidebar(); }} disabled={reviewsLoading}>
              <span className="adm-sidebar-icon">↻</span> Refresh Reviews
            </button>
          </>)}
        </div>

        <div className="adm-sidebar-footer">
          <button type="button" className="adm-sidebar-btn" onClick={handleLogout}>
            <span className="adm-sidebar-icon">⏏</span> Log out
          </button>
        </div>
      </aside>

      <div className="adm-main">
        <div className="adm-topbar">
          <button type="button" className="adm-hamburger" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle menu">☰</button>
          <span className="adm-topbar-title">E-Invitation Admin</span>
        </div>

        <div className="adm-page">
          {/* inline alerts replaced by floating toast — see bottom of component */}

          {/* ══ INVITATIONS TAB ══════════════════════════ */}
          {activeTab === 'invitations' && (<>

            <div className="adm-page-header">
              <div>
                <p className="adm-page-eyebrow">Invitation Admin</p>
                <h1 className="adm-page-title">Dashboard</h1>
                <p className="adm-page-description">Manage invitations, RSVP email settings, and access codes.</p>
              </div>
              <div className="adm-page-actions">
                <button type="button" onClick={() => fetchCodes(token)} disabled={loading} className="adm-btn adm-btn-secondary adm-btn-sm">↻ Refresh</button>
                <button type="button" onClick={syncDesignInvitations} disabled={loading} className="adm-btn adm-btn-secondary adm-btn-sm">Sync Designs</button>
                <button type="button" onClick={() => { setCreatedInvitationRow(null); setShowAddForm((v) => !v); }} disabled={loading} className="adm-btn adm-btn-primary adm-btn-sm">
                  {showAddForm ? '✕ Cancel' : '＋ Add Invitation'}
                </button>
              </div>
            </div>

            {dashboardStats.hasData && (
              <div className="adm-stats-grid">
                <StatCard label="Total Invitations" value={dashboardStats.totalInvitations} color="#7c2d12" />
                <StatCard label="Total RSVPs" value={dashboardStats.totalRsvps} color="#b45309" />
                <StatCard label="With RSVPs" value={dashboardStats.invitationsWithRsvps} color="#0f766e" />
                <StatCard label="No RSVPs Yet" value={dashboardStats.invitationsWithoutRsvps} color="#9a8a7e" />
                <StatCard label="Active Categories" value={dashboardStats.activeCategories} color="#1d4ed8" />
                <StatCard label="Invitations w/ Codes" value={dashboardStats.invitationsWithCodes} color="#9333ea" />
              </div>
            )}

            {dashboardStats.hasData && (
              <div className="adm-charts-grid">
                <div className="adm-chart-card">
                  <div className="adm-chart-title">By Category</div>
                  <div className="adm-chart-body"><Doughnut data={categoryChartData} options={doughnutOptions} /></div>
                </div>
                <div className="adm-chart-card">
                  <div className="adm-chart-title">RSVP Status Split</div>
                  <div className="adm-chart-body"><Doughnut data={responseSplitChartData} options={doughnutOptions} /></div>
                </div>
                <div className="adm-chart-card adm-chart-card--full">
                  <div className="adm-chart-title">Top 6 Invitations by RSVPs</div>
                  <div className="adm-chart-body"><Bar data={topInvitationsChartData} options={barOptions} /></div>
                </div>
              </div>
            )}

            <form onSubmit={ensureCode} className="adm-lookup-bar">
              <div className="adm-lookup-input-wrap adm-field">
                <label className="adm-label">Generate / Look up Access Code</label>
                <input className="adm-input" type="text" value={invitationId} onChange={(e) => setInvitationId(e.target.value)} placeholder="Invitation ID (e.g. birthday-maria-santos)" required />
              </div>
              <button type="submit" disabled={loading} className="adm-btn adm-btn-primary" style={{ alignSelf: 'flex-end' }}>
                {loading ? 'Working…' : 'Generate / Get Code'}
              </button>
            </form>

            <div className="adm-section adm-inv-section">
              <div className="adm-section-header">
                <h2 className="adm-section-title">All Invitations</h2>
                <span className="adm-section-count">{rows.length} total</span>
              </div>
              {loading && rows.length === 0 ? (
                <div className="adm-loading">Loading invitations…</div>
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Invitation ID</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>RSVP Email</th>
                        <th>Event Date</th>
                        <th>RSVP</th>
                        <th>Code</th>
                        <th>RSVPs</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={10} className="adm-table-empty">No invitation records yet. Click <strong>Sync Designs</strong> or <strong>Add Invitation</strong> to get started.</td></tr>
                      ) : rows.map((row) => {
                        const effectivelyClosed = isRowRsvpEffectivelyClosed(row);
                        const autoClosed = !row.rsvpClosed && effectivelyClosed; // closed only because event date passed
                        return (
                          <tr key={row.invitationId}>
                            <td className="adm-table-id">{row.invitationId}</td>
                            <td>{row.title || <span style={{ color: 'var(--adm-muted)', fontStyle: 'italic' }}>—</span>}</td>
                            <td><span className="adm-table-badge">{row.categoryLabel || row.category || '—'}</span></td>
                            <td style={{ fontSize: '12px' }}>{row.rsvpEmail || '—'}</td>
                            <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{row.eventDate ? new Date(row.eventDate + 'T00:00:00').toLocaleDateString() : '—'}</td>
                            <td>
                              {effectivelyClosed ? (
                                <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                                  {autoClosed ? 'Auto-closed' : 'Closed'}
                                </span>
                              ) : (
                                <span style={{ background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>Open</span>
                              )}
                            </td>
                            <td><code className="adm-table-code">{row.accessCode || '—'}</code></td>
                            <td style={{ textAlign: 'center' }}>{row.totalRsvps ?? 0}</td>
                            <td style={{ fontSize: '11px', color: 'var(--adm-muted)', whiteSpace: 'nowrap' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}</td>
                            <td>
                              <div className="adm-row-actions">
                                <a href={`/invitations/custom/${row.invitationId}`} target="_blank" rel="noreferrer" className="adm-btn adm-btn-secondary adm-btn-sm">👁 View ↗</a>
                                <button type="button" onClick={() => openEditDrawer(row)} className="adm-btn adm-btn-secondary adm-btn-sm">✏ Edit</button>
                                {effectivelyClosed ? (
                                  <button type="button" onClick={() => toggleRsvpClosed(row, false)} disabled={loading} className="adm-btn adm-btn-secondary adm-btn-sm">✓ Open RSVP</button>
                                ) : (
                                  <button type="button" onClick={() => setRsvpCloseTarget(row)} disabled={loading} className="adm-btn adm-btn-danger adm-btn-sm">✕ Close RSVP</button>
                                )}
                                <button type="button" onClick={() => openRegenerateConfirmation(row.invitationId)} disabled={loading} className="adm-btn adm-btn-secondary adm-btn-sm">🔑 Regen</button>
                                {row.accessCode && (
                                  <a href={getCheckerLink(row.accessCode)} target="_blank" rel="noreferrer" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => prepareAdminCheckerLink(row.accessCode)}>🪑 Seat Checker ↗</a>
                                )}
                                {row.canDelete && (
                                  <button type="button" onClick={() => openDeleteConfirmation(row)} disabled={loading} className="adm-btn adm-btn-danger adm-btn-sm">🗑 Delete</button>
                                )}
                                {String(row.integrationStatus || '').toLowerCase() === 'awaiting_setup' && (
                                  <button type="button" onClick={() => openDeveloperGuideForInvitation(row)} className="adm-btn adm-btn-secondary adm-btn-sm">📋 Dev Guide</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </>)} {/* end invitations tab */}

          {/* ══ BOOKINGS TAB ══════════════════════════════ */}
          {activeTab === 'bookings' && (<>

            <div className="adm-page-header">
              <div>
                <p className="adm-page-eyebrow">Booking Admin</p>
                <h1 className="adm-page-title">Bookings</h1>
                <p className="adm-page-description">Track client bookings, update status, and view analytics.</p>
              </div>
              <div className="adm-page-actions">
                <button type="button" onClick={() => fetchBookings(token, bookingView)} disabled={bookingsLoading} className="adm-btn adm-btn-secondary adm-btn-sm">↻ Refresh</button>
                {isPushAvailable() ? (
                  <button type="button" onClick={enablePushNotifications} className={`adm-btn adm-btn-sm ${pushRegistered ? 'adm-btn-secondary' : 'adm-btn-primary'}`}>
                    🔔 {pushRegistered ? 'Push On ✓' : 'Enable Push'}
                  </button>
                ) : (
                  <span title="Push notifications require HTTPS" style={{ fontSize: '11px', color: 'var(--adm-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    🔔 Push requires HTTPS
                  </span>
                )}
              </div>
            </div>

            {bookingStats && (
              <div className="adm-stats-grid" style={{ marginBottom: '24px' }}>
                <StatCard label="Total Bookings" value={bookingStats.totals?.total ?? 0} color="#7c2d12" />
                <StatCard label="Pending" value={bookingStats.totals?.pending ?? 0} color="#b45309" />
                <StatCard label="In Progress" value={bookingStats.totals?.in_progress ?? 0} color="#1d4ed8" />
                <StatCard label="Completed" value={bookingStats.totals?.completed ?? 0} color="#0f766e" />
                <StatCard label="Template Orders" value={bookingStats.totals?.template_count ?? 0} color="#7c3aed" />
                <StatCard label="Custom Orders" value={bookingStats.totals?.custom_count ?? 0} color="#be185d" />
                <StatCard label="Archived" value={bookingStats.totals?.archived ?? 0} color="#6b7280" />
              </div>
            )}

            {bookingChartData && (
              <div className="adm-charts-grid" style={{ marginBottom: '24px' }}>
                <div className="adm-chart-card">
                  <div className="adm-chart-title">Bookings by Event Type</div>
                  <div className="adm-chart-body"><Bar data={bookingChartData.eventTypeBar} options={barOptions} /></div>
                </div>
                <div className="adm-chart-card">
                  <div className="adm-chart-title">Status Distribution</div>
                  <div className="adm-chart-body"><Doughnut data={bookingChartData.statusDoughnut} options={doughnutOptions} /></div>
                </div>
                <div className="adm-chart-card adm-chart-card--full">
                  <div className="adm-chart-title">Bookings per Month (Last 12)</div>
                  <div className="adm-chart-body"><Bar data={bookingChartData.monthLine} options={barOptions} /></div>
                </div>
              </div>
            )}

            {bookingChartData && bookingChartData.sourceRows.length > 0 && (
              <div className="adm-section" style={{ marginBottom: '24px' }}>
                <div className="adm-section-header">
                  <h2 className="adm-section-title">📣 How Did You Hear About Us</h2>
                  <span className="adm-section-count">{bookingChartData.sourceRows.length} source{bookingChartData.sourceRows.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="adm-source-grid">
                  <div style={{ padding: '16px 20px', borderRight: '1px solid var(--adm-border-light)' }}>
                    <div style={{ height: '220px' }}>
                      <Bar data={bookingChartData.sourceBar} options={{
                        ...barOptions,
                        indexAxis: 'y',
                        plugins: { ...barOptions.plugins, legend: { display: false } },
                        scales: {
                          x: { ...barOptions.scales.x, beginAtZero: true, ticks: { ...barOptions.scales.x.ticks, precision: 0 }, grid: { color: CHART_GRID_COLOR } },
                          y: { ...barOptions.scales.y, grid: { display: false } }
                        }
                      }} />
                    </div>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--adm-border-light)' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--adm-muted)', fontFamily: 'var(--adm-font-ui)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Source</th>
                          <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--adm-muted)', fontFamily: 'var(--adm-font-ui)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Count</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--adm-muted)', fontFamily: 'var(--adm-font-ui)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const total = bookingChartData.sourceRows.reduce((s, r) => s + Number(r.cnt), 0);
                          return bookingChartData.sourceRows.map((r, i) => {
                            const pct = total > 0 ? Math.round((Number(r.cnt) / total) * 100) : 0;
                            const color = DASHBOARD_PALETTE[i % DASHBOARD_PALETTE.length];
                            return (
                              <tr key={r.source} style={{ borderBottom: '1px solid var(--adm-border-light)' }}>
                                <td style={{ padding: '8px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', background: color, flexShrink: 0 }} />
                                  {r.source}
                                </td>
                                <td style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 700, color: 'var(--adm-text-base)' }}>{r.cnt}</td>
                                <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                    <div style={{ width: '60px', height: '6px', borderRadius: '3px', background: 'var(--adm-border-light)', overflow: 'hidden' }}>
                                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
                                    </div>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--adm-muted)', minWidth: '32px', textAlign: 'right' }}>{pct}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="adm-section adm-bookings-section">
              <div className="adm-section-header">
                <h2 className="adm-section-title">{bookingView === 'archived' ? 'Archived Bookings' : 'Active Bookings'}</h2>
                <span className="adm-section-count">{bookings.length} total</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', padding: '0 16px 14px', flexWrap: 'wrap' }}>
                {[
                  { key: 'active', label: `Active (${bookingStats?.totals?.total ?? 0})` },
                  { key: 'archived', label: `Archived (${bookingStats?.totals?.archived ?? 0})` }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`adm-rv-filter-pill${bookingView === item.key ? ' active' : ''}`}
                    onClick={() => selectBookingView(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {bookingsLoading ? (
                <div className="adm-loading">Loading bookings…</div>
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>#</th><th>Client</th><th>Event</th><th>Date</th><th>Design</th><th>Template</th><th>Status</th><th>Submitted</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 ? (
                        <tr><td colSpan={9} className="adm-table-empty">{bookingView === 'archived' ? 'No archived bookings yet.' : 'No active bookings yet.'}</td></tr>
                      ) : bookings.map((b) => (
                        <React.Fragment key={b.id}>
                          <tr
                            className={openStatusMenu === b.id ? 'adm-booking-row--status-open' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedBooking(expandedBooking === b.id ? null : b.id)}
                          >
                            <td className="adm-table-id">#{b.id}</td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: '13px' }}>{b.full_name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--adm-muted)' }}>{b.email}</div>
                              {b.phone && <div style={{ fontSize: '11px', color: 'var(--adm-muted)' }}>{b.phone}</div>}
                            </td>
                            <td>{capitalize(b.event_type || '—')}</td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{b.event_date ? new Date(b.event_date).toLocaleDateString() : '—'}</td>
                            <td>
                              <span className="adm-table-badge" style={{ background: b.design_type === 'template' ? '#ede9fe' : '#fef3c7', color: b.design_type === 'template' ? '#6d28d9' : '#92400e' }}>
                                {b.design_type}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {b.template_name || b.selected_template || '—'}
                            </td>
                            <td>
                              <BookingStatusMenu
                                status={b.status}
                                disabled={bookingStatusUpdating === b.id}
                                readOnly={bookingView === 'archived'}
                                isOpen={openStatusMenu === b.id}
                                onToggle={() => setOpenStatusMenu(openStatusMenu === b.id ? null : b.id)}
                                onChange={(nextStatus) => requestBookingStatusChange(b, nextStatus)}
                              />
                            </td>
                            <td style={{ fontSize: '11px', whiteSpace: 'nowrap', color: 'var(--adm-muted)' }}>
                              {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td>
                              <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={(e) => { e.stopPropagation(); setExpandedBooking(expandedBooking === b.id ? null : b.id); }}>
                                {expandedBooking === b.id ? '▲ Less' : '▼ More'}
                              </button>
                            </td>
                          </tr>
                          {expandedBooking === b.id && (
                            <tr className="adm-row-expanded">
                              <td colSpan={9}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 24px', padding: '12px 0', fontSize: '12px' }}>
                                  {[
                                    ['Venue', b.venue],
                                    ['Event Time', b.event_time],
                                    ['Facebook', b.facebook_account],
                                    ['Theme', b.theme],
                                    ['Color Pref.', b.color_preference],
                                    ['Celebrant', b.celebrant_name],
                                    ['Parents', b.parents_names],
                                    ['Dress Code', b.dress_code],
                                    ['RSVP Email', b.rsvp_email],
                                    ['RSVP Deadline', b.rsvp_deadline],
                                    ['Music', b.music_title],
                                    ['How did you hear?', b.how_did_you_hear],
                                    ['Price', b.template_price ? `₱${Number(b.template_price).toLocaleString()}` : null]
                                  ].filter(([, v]) => v).map(([label, val]) => (
                                    <div key={label}>
                                      <span style={{ color: 'var(--adm-muted)', fontWeight: 600, display: 'block', marginBottom: '2px' }}>{label}</span>
                                      <span>{val}</span>
                                    </div>
                                  ))}
                                  {b.special_instructions && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <span style={{ color: 'var(--adm-muted)', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Special Instructions</span>
                                      <span>{b.special_instructions}</span>
                                    </div>
                                  )}
                                  {b.welcome_message && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <span style={{ color: 'var(--adm-muted)', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Welcome Message</span>
                                      <span>{b.welcome_message}</span>
                                    </div>
                                  )}
                                </div>
                                {bookingView === 'active' && (() => {
                                  const _suggestedId = normalizeInvitationId(
                                    `${b.celebrant_name || b.full_name || ''}-${b.event_type || ''}`.replace(/\s+/g, '-')
                                  );
                                  const _linkedInvRow = (b.linked_invitation_id && rows.find((r) => r.invitationId === b.linked_invitation_id))
                                    ? rows.find((r) => r.invitationId === b.linked_invitation_id)
                                    : (rows.find((r) => r.invitationId === _suggestedId) || null);

                                  return (
                                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--adm-border)' }}>
                                      {_linkedInvRow ? (
                                        (() => {
                                          const status = String(_linkedInvRow.integrationStatus || '').toLowerCase();
                                          if (status === 'integrated') {
                                            return (
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                <span style={{
                                                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                  fontSize: '12px', fontWeight: 700, borderRadius: '20px',
                                                  padding: '4px 12px', border: '1.5px solid #86efac',
                                                  background: '#f0fdf4', color: '#166534'
                                                }}>
                                                  ✓ Invitation Created
                                                </span>
                                                <a
                                                  href={`/invitations/custom/${_linkedInvRow.invitationId}`}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="adm-btn adm-btn-secondary adm-btn-sm"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  View Invitation ↗
                                                </a>
                                              </div>
                                            );
                                          }

                                          if (status === 'awaiting_production' || status === 'local_integrated') {
                                            return (
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                <span style={{
                                                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                  fontSize: '12px', fontWeight: 700, borderRadius: '20px',
                                                  padding: '4px 12px', border: '1.5px solid #facc15',
                                                  background: '#fffbeb', color: '#92400e'
                                                }}>
                                                  ⏳ Local setup complete — awaiting deployment
                                                </span>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                              <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                fontSize: '12px', fontWeight: 700, borderRadius: '20px',
                                                padding: '4px 12px', border: '1.5px solid #cbd5e1',
                                                background: '#f8fafc', color: '#475569'
                                              }}>
                                                ⏳ Awaiting setup
                                              </span>
                                              <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={(e) => { e.stopPropagation(); openDeveloperGuideForInvitation(_linkedInvRow); }}>
                                                📋 Dev Guide
                                              </button>
                                            </div>
                                          );
                                        })()
                                      ) : canCreateInvitationFromBooking(b) ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                          <button
                                            type="button"
                                            className="adm-btn adm-btn-primary adm-btn-sm"
                                            onClick={(e) => { e.stopPropagation(); openAddFormFromBooking(b); }}
                                          >
                                            ＋ Create Invitation from this Booking
                                          </button>
                                          {!b.event_date && (
                                            <span style={{ fontSize: '11px', color: 'var(--adm-danger)' }}>⚠ No event date on booking — please set it manually in the form.</span>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px', paddingTop: '10px', borderTop: bookingView === 'active' && canCreateInvitationFromBooking(b) ? '0' : '1px solid var(--adm-border)' }}>
                                  {bookingView === 'active' && getBookingStatus(b) === 'completed' && (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                                      fontSize: '11px', fontWeight: 700, borderRadius: '20px',
                                      padding: '3px 10px', border: '1.5px solid',
                                      ...(b.has_review
                                        ? { background: '#f0fdf4', color: '#166534', borderColor: '#86efac' }
                                        : { background: '#fef9ee', color: '#92400e', borderColor: '#fde68a' })
                                    }}>
                                      {b.has_review ? '✓ Reviewed' : '○ Not Yet Reviewed'}
                                    </span>
                                  )}
                                  {bookingView === 'archived' && (
                                    <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('unarchive', b); }}>
                                      Restore to Active
                                    </button>
                                  )}
                                  {bookingView === 'active' && getBookingStatus(b) === 'completed' && !b.has_review && (
                                    <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('resendReview', b); }}>
                                      Resend Review Request
                                    </button>
                                  )}
                                  {bookingView === 'active' && ['completed', 'cancelled'].includes(getBookingStatus(b)) && (
                                    <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('archive', b); }}>
                                      Archive Booking
                                    </button>
                                  )}
                                  {bookingView === 'active' && ['pending', 'in_progress'].includes(getBookingStatus(b)) && (
                                    <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('cancel', b); }}>
                                      Cancel Booking
                                    </button>
                                  )}
                                  {bookingView === 'active' && getBookingStatus(b) === 'cancelled' && (
                                    <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('restore', b); }}>
                                      Restore / Reopen
                                    </button>
                                  )}
                                  {getBookingStatus(b) !== 'completed' && canDeleteBooking(b) && (
                                    <button type="button" className="adm-btn adm-btn-danger adm-btn-sm" onClick={(e) => { e.stopPropagation(); requestBookingAction('delete', b); }}>
                                      {getBookingStatus(b) === 'cancelled' ? 'Delete Booking' : 'Delete Test/Mistake'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </>)} {/* end bookings tab */}

          {/* ══ REVIEWS TAB ═══════════════════════════ */}
          {activeTab === 'reviews' && (<>

            <div className="adm-page-header">
              <div>
                <p className="adm-page-eyebrow">Review Admin</p>
                <h1 className="adm-page-title">Customer Reviews</h1>
                <p className="adm-page-description">Moderate, reply to, and manage customer feedback.</p>
              </div>
              <div className="adm-page-actions">
                <button type="button" onClick={() => fetchReviews(token)} disabled={reviewsLoading} className="adm-btn adm-btn-secondary adm-btn-sm">↻ Refresh</button>
                <button type="button" onClick={openManualReviewModal} className="adm-btn adm-btn-primary adm-btn-sm">＋ Add Review</button>
              </div>
            </div>

            <div className="adm-stats-grid">
              <StatCard label="Total Reviews" value={reviewComputedStats.total} color="#7c2d12" />
              <StatCard label="Published" value={reviewComputedStats.published} color="#0f766e" />
              <StatCard label="Hidden" value={reviewComputedStats.hidden} color="#b45309" />
              <StatCard label="Avg Rating ★" value={reviewComputedStats.avgRating ?? '—'} color="#9333ea" />
            </div>

            <div className="adm-rv-filterbar">
              {[
                { key: 'all', label: `All (${reviewComputedStats.total})` },
                { key: 'published', label: `✅ Published (${reviewComputedStats.published})` },
                { key: 'hidden', label: `🙈 Hidden (${reviewComputedStats.hidden})` }
              ].map(f => (
                <button key={f.key} type="button"
                  className={`adm-rv-filter-pill${reviewFilter === f.key ? ' active' : ''}`}
                  onClick={() => setReviewFilter(f.key)}
                >{f.label}</button>
              ))}
            </div>

            {reviewsLoading ? (
              <div className="adm-loading">Loading reviews…</div>
            ) : filteredReviews.length === 0 ? (
              <div className="adm-section">
                <div className="adm-table-empty" style={{ padding: '48px 20px' }}>
                  {reviews.length === 0
                    ? 'No reviews yet. They are sent automatically when a booking is marked as completed.'
                    : 'No reviews match this filter.'}
                </div>
              </div>
            ) : (
              <div className="adm-rv-list">
                {filteredReviews.map(review => {
                  const isActing = reviewActionLoading === review.id;
                  const isReplySaving = reviewActionLoading === `reply-${review.id}`;
                  const isReplyEmailSending = reviewActionLoading === `reply-email-${review.id}`;
                  const replyDraft = reviewReplyDraft[review.id] ?? (review.admin_reply || '');
                  const cardSaveStatus = reviewSaveStatus[review.id] || null;
                  const eventDate = review.event_date ? new Date(review.event_date).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                  const submittedAt = review.submitted_at ? new Date(review.submitted_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                  return (
                    <div key={review.id} className={`adm-rv-card${review.is_published ? ' adm-rv-card--published' : ''}`}>

                      {/* Head: stars + badge */}
                      <div className="adm-rv-card-head">
                        <div className="adm-rv-stars-row">
                          {[1, 2, 3, 4, 5].map(i => (
                            <span key={i} className={`adm-rv-star${i <= Number(review.rating) ? ' filled' : ''}`}>★</span>
                          ))}
                          <span className="adm-rv-rating-num">{Number(review.rating).toFixed(1)}</span>
                        </div>
                        <span className={`adm-rv-badge ${review.is_published ? 'adm-rv-badge--pub' : 'adm-rv-badge--hid'}`}>
                          {review.is_published ? '✅ Published' : '🙈 Hidden'}
                        </span>
                      </div>

                      {/* Reviewer meta */}
                      <div className="adm-rv-meta">
                        <span className="adm-rv-name">{review.reviewer_name}</span>
                        <span className="adm-rv-sep">·</span>
                        <span>{capitalize(review.event_type || '—')}</span>
                        {review.template_name && (<><span className="adm-rv-sep">·</span><span>{review.template_name}</span></>)}
                        <span className="adm-rv-sep">·</span>
                        <span>Event: {eventDate}</span>
                        <span className="adm-rv-sep">·</span>
                        <span className="adm-rv-submitted">Reviewed: {submittedAt}</span>
                      </div>

                      {/* Comment */}
                      {review.comment
                        ? <p className="adm-rv-comment">&ldquo;{review.comment}&rdquo;</p>
                        : <p className="adm-rv-comment adm-rv-comment--empty">No written comment provided.</p>
                      }

                      {/* Review images */}
                      {(() => {
                        let imgs = [];
                        try { imgs = review.review_images ? JSON.parse(review.review_images) : []; } catch { imgs = []; }
                        if (!imgs.length) return null;
                        const base = getApi('');
                        return (
                          <div className="adm-rv-images">
                            {imgs.map((src, i) => (
                              <a key={i} href={`${base}${src}`} target="_blank" rel="noopener noreferrer" className="adm-rv-image-thumb">
                                <img src={`${base}${src}`} alt={`Review image ${i + 1}`} />
                              </a>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Aspect ratings */}
                      {(review.rating_design || review.rating_communication || review.rating_value) && (
                        <div className="adm-rv-aspects">
                          {review.rating_design && <div className="adm-rv-aspect"><span className="adm-rv-aspect-label">Design</span> {'★'.repeat(review.rating_design)}{'☆'.repeat(5 - review.rating_design)}</div>}
                          {review.rating_communication && <div className="adm-rv-aspect"><span className="adm-rv-aspect-label">Communication</span> {'★'.repeat(review.rating_communication)}{'☆'.repeat(5 - review.rating_communication)}</div>}
                          {review.rating_value && <div className="adm-rv-aspect"><span className="adm-rv-aspect-label">Value</span> {'★'.repeat(review.rating_value)}{'☆'.repeat(5 - review.rating_value)}</div>}
                        </div>
                      )}

                      {/* Admin reply */}
                      <div className="adm-rv-reply-section">
                        <label className="adm-rv-reply-label">Admin Reply</label>

                        {/* Saved reply preview */}
                        {review.admin_reply && (
                          <div className="adm-rv-saved-reply">
                            <span className="adm-rv-saved-reply-label">Currently saved:</span>
                            <p className="adm-rv-saved-reply-text">{review.admin_reply}</p>
                          </div>
                        )}

                        <div className="adm-rv-reply-row">
                          <textarea
                            className="adm-input adm-rv-reply-input"
                            placeholder="Write a reply to this review…"
                            rows={2}
                            maxLength={2000}
                            value={replyDraft}
                            onChange={e => {
                              setReviewReplyDraft(prev => ({ ...prev, [review.id]: e.target.value }));
                              if (reviewSaveStatus[review.id]) {
                                setReviewSaveStatus(prev => { const next = { ...prev }; delete next[review.id]; return next; });
                              }
                            }}
                          />
                          <button type="button"
                            className="adm-btn adm-btn-secondary adm-btn-sm adm-rv-reply-btn"
                            disabled={isReplySaving || isReplyEmailSending}
                            onClick={() => saveReviewReply(review.id)}
                          >{isReplySaving ? 'Saving…' : 'Save Reply'}</button>
                          {replyEmailConfirmId === review.id ? (
                            <span className="adm-rv-email-confirm">
                              <span className="adm-rv-email-confirm-msg">Send email to <strong>{review.reviewer_email || 'customer'}</strong>?</span>
                              <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" disabled={isReplyEmailSending}
                                onClick={() => { setReplyEmailConfirmId(null); sendReviewReplyEmail(review.id); }}>
                                {isReplyEmailSending ? 'Sending…' : 'Yes, Send'}
                              </button>
                              <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => setReplyEmailConfirmId(null)}>Cancel</button>
                            </span>
                          ) : (
                            <button type="button"
                              className="adm-btn adm-btn-primary adm-btn-sm adm-rv-reply-btn"
                              disabled={isReplySaving || isReplyEmailSending || !replyDraft.trim()}
                              onClick={() => setReplyEmailConfirmId(review.id)}
                              title="Save this reply and send it to the reviewer by email"
                            >📧 Send Reply by Email</button>
                          )}
                        </div>
                        <div className="adm-rv-reply-footer">
                          <span style={{ fontSize: '0.72rem', color: '#9a8a7e' }}>{replyDraft.length} / 2000</span>
                          {cardSaveStatus && (
                            <span className={`adm-rv-save-status adm-rv-save-status--${cardSaveStatus.type}`}>
                              {cardSaveStatus.type === 'success' ? '✓ ' : '⚠ '}{cardSaveStatus.msg}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="adm-rv-actions">
                        <button type="button"
                          className={`adm-btn adm-btn-sm ${review.is_published ? 'adm-btn-secondary' : 'adm-btn-primary'}`}
                          disabled={isActing}
                          onClick={() => togglePublishReview(review.id, !review.is_published)}
                        >
                          {isActing ? '…' : review.is_published ? '🙈 Hide' : '✅ Publish'}
                        </button>
                        <button type="button"
                          className="adm-btn adm-btn-danger adm-btn-sm"
                          disabled={isActing}
                          onClick={() => setDeleteReviewTarget(review.id)}
                        >🗑 Delete</button>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </>)} {/* end reviews tab */}

        </div>
      </div>

      {/* ── Add Invitation Offcanvas (outside all tabs — works from any tab) ── */}
      {showAddForm && (
        <>
          <div className="adm-offcanvas-backdrop" onClick={() => { setShowAddForm(false); setSourceBookingId(null); }} />
          <aside className="adm-offcanvas" role="dialog" aria-modal="true" aria-label="Add New Invitation">
            <div className="adm-offcanvas-header">
              <h2 className="adm-offcanvas-title">＋ Add New Invitation</h2>
              <button className="adm-offcanvas-close" onClick={() => { setShowAddForm(false); setSourceBookingId(null); }} aria-label="Close">✕</button>
            </div>

            <div className="adm-offcanvas-body">
              {/* ── Form ── */}
              <div>
                <p className="adm-form-section-title">Invitation Details</p>
                {activeCreatedRow && (
                  <div style={{ marginBottom: '10px', fontSize: '13px', color: 'var(--adm-muted)' }}>
                    {(() => {
                      const s = String(activeCreatedRow.integrationStatus || activeCreatedRow.integration_status || '').toLowerCase();
                      if (s === 'integrated') return 'This invitation is integrated — fields are read-only.';
                      if (s === 'awaiting_production' || s === 'local_integrated') return 'Local setup complete — fields are read-only.';
                      return 'This invitation already exists — fields are read-only.';
                    })()}
                  </div>
                )}
                <form id="add-invitation-form" onSubmit={addInvitation}>
                  <div className="adm-add-grid">
                    <div className="adm-add-row">
                      <div className="adm-field">
                        <label className="adm-label">Invitation ID *</label>
                        <input className="adm-input" type="text" value={newInvitationId} onChange={(e) => setNewInvitationId(e.target.value)} placeholder="e.g. birthday-maria-santos" required disabled={Boolean(activeCreatedRow)} />
                        {pendingInvitationId && <p className="adm-helper">Normalized: <code>{pendingInvitationId}</code></p>}
                      </div>
                      <div className="adm-field">
                        <label className="adm-label">Category *</label>
                        <select className="adm-input" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} required disabled={Boolean(activeCreatedRow)}>
                          {INVITATION_CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="adm-add-row">
                      <div className="adm-field">
                        <label className="adm-label">Title <span style={{ color: 'var(--adm-text-subtle)', fontWeight: 400 }}>(optional)</span></label>
                        <input className="adm-input" type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Maria's 18th Birthday" disabled={Boolean(activeCreatedRow)} />
                      </div>
                      <div className="adm-field">
                        <label className="adm-label">RSVP Email *</label>
                        <input className="adm-input" type="email" value={newRsvpEmail} onChange={(e) => setNewRsvpEmail(e.target.value)} placeholder={selectedCategoryOption.emailExample} required disabled={Boolean(activeCreatedRow)} />
                      </div>
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Event Date *</label>
                      <input className="adm-input" type="date" value={newEventDate} onChange={(e) => setNewEventDate(e.target.value)} required disabled={Boolean(activeCreatedRow)} />
                      <p className="adm-helper">RSVPs close automatically after this date.</p>
                    </div>
                    {pendingInvitationId && (
                      <div className="adm-field">
                        <p className="adm-helper">
                          Design file: <code>{pendingDesignFilePath}</code>
                          &nbsp;·&nbsp; Preview: <code>{pendingPreviewPath}</code>
                        </p>
                      </div>
                    )}
                    {offcanvasError && <div className="adm-alert adm-alert-error" style={{ marginTop: '8px' }}>{offcanvasError}</div>}
                  </div>
                </form>
              </div>

              {/* ── Developer Guide ── */}
              <div className="adm-guide">
                <p className="adm-guide-title">📋 Developer Guide — Live Preview</p>
                {(pendingInvitationId || displayedCode) ? (
                  <>
                    <div className="adm-guide-steps" style={{ marginTop: '12px' }}>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">1</span>
                        <p className="adm-guide-text">
                          <strong>Create the file</strong> at <code>{`e-inv-app/src/pages/invitations/designs/${displayedFileName}`}</code> — or run one of the commands below.
                          <code style={{ display: 'block', marginTop: '6px' }}>{`# macOS / Linux / Git Bash\nmkdir -p e-inv-app/src/pages/invitations/designs && touch e-inv-app/src/pages/invitations/designs/${displayedFileName}`}</code>
                          <code style={{ display: 'block', marginTop: '6px' }}>{`# PowerShell (Windows)\nNew-Item -ItemType Directory -Force -Path 'e-inv-app/src/pages/invitations/designs'; New-Item -ItemType File -Force -Path 'e-inv-app/src/pages/invitations/designs/${displayedFileName}'`}</code>
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => copyToClipboard(`mkdir -p e-inv-app/src/pages/invitations/designs && touch e-inv-app/src/pages/invitations/designs/${displayedFileName}`, 'Unix command copied to clipboard')}>Copy Unix Command</button>
                        <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => copyToClipboard(`New-Item -ItemType Directory -Force -Path 'e-inv-app/src/pages/invitations/designs'; New-Item -ItemType File -Force -Path 'e-inv-app/src/pages/invitations/designs/${displayedFileName}'`, 'PowerShell command copied to clipboard')}>Copy PowerShell Command</button>
                      </div>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">2</span>
                        <p className="adm-guide-text">Copy the code from the box below into that file.</p>
                      </div>
                      <textarea id="adm-generated-code-textarea" readOnly value={displayedCode} className="adm-input" style={{ fontFamily: 'monospace', whiteSpace: 'pre', minHeight: '260px' }} />
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => { if (displayedCode) { navigator.clipboard?.writeText(displayedCode); setToast({ type: 'success', msg: 'Code copied to clipboard' }); } }}>Copy Code</button>
                      </div>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">3</span>
                        <p className="adm-guide-text">Click "Create Invitation" after creating the file.</p>
                      </div>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">4</span>
                        <p className="adm-guide-text">You can now customize the invitation code template (layout, styles, RSVP form).</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="adm-guide-steps">
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">1</span>
                        <p className="adm-guide-text">
                          <strong>Create the file</strong> at<br />
                          <code>e-inv-app/src/pages/invitations/designs/&lt;invitation-id&gt;.jsx</code> — or run one of the commands below.
                          <code style={{ display: 'block', marginTop: '6px' }}>{`# macOS / Linux / Git Bash\nmkdir -p e-inv-app/src/pages/invitations/designs && touch e-inv-app/src/pages/invitations/designs/<invitation-id>.jsx`}</code>
                          <code style={{ display: 'block', marginTop: '6px' }}>{`# PowerShell (Windows)\nNew-Item -ItemType Directory -Force -Path 'e-inv-app/src/pages/invitations/designs'; New-Item -ItemType File -Force -Path 'e-inv-app/src/pages/invitations/designs/<invitation-id>.jsx'`}</code>
                        </p>
                      </div>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">2</span>
                        <p className="adm-guide-text">Copy the generated code (or use the live preview) and paste it into the file.</p>
                      </div>
                      <div className="adm-guide-step">
                        <span className="adm-guide-num">3</span>
                        <p className="adm-guide-text">You can now customize the invitation code template (layout, styles, RSVP form).</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', margin: '10px 0' }}>
                      <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => copyToClipboard(`mkdir -p e-inv-app/src/pages/invitations/designs && touch e-inv-app/src/pages/invitations/designs/<invitation-id>.jsx`, 'Unix command copied to clipboard')}>Copy Unix Command</button>
                      <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={() => copyToClipboard(`New-Item -ItemType Directory -Force -Path 'e-inv-app/src/pages/invitations/designs'; New-Item -ItemType File -Force -Path 'e-inv-app/src/pages/invitations/designs/<invitation-id>.jsx'`, 'PowerShell command copied to clipboard')}>Copy PowerShell Command</button>
                    </div>
                    <div className="adm-guide-note">
                      ⚠️ If your server is configured to write design files, it may create the file automatically; otherwise create it manually and paste the generated code.
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="adm-offcanvas-footer">
              {(() => {
                const existing = createdInvitationRow || rows.find((r) => r.invitationId === (pendingInvitationId || newInvitationId));
                if (existing) {
                  const status = String(existing.integrationStatus || existing.integration_status || '').toLowerCase();
                  if (status === 'integrated') {
                    return (
                      <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, borderRadius: '20px', padding: '6px 12px', border: '1.5px solid #86efac', background: '#f0fdf4', color: '#166534' }}>
                            ✓ Invitation Created
                          </span>
                        </div>
                        <div>
                          <a href={`/invitations/custom/${existing.invitationId}`} target="_blank" rel="noreferrer" className="adm-btn adm-btn-secondary">View Invitation ↗</a>
                        </div>
                      </div>
                    );
                  }

                  if (status === 'awaiting_production' || status === 'local_integrated') {
                    return (
                      <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, borderRadius: '20px', padding: '6px 12px', border: '1.5px solid #facc15', background: '#fffbeb', color: '#92400e' }}>
                            ⏳ Local setup complete — awaiting deployment
                          </span>
                        </div>
                        <div>
                          <button type="button" onClick={() => { setShowAddForm(false); setSourceBookingId(null); setCreatedInvitationRow(null); }} className="adm-btn adm-btn-secondary">Close</button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, borderRadius: '20px', padding: '6px 12px', border: '1.5px solid #cbd5e1', background: '#f8fafc', color: '#475569' }}>
                          ⏳ Awaiting setup
                        </span>
                      </div>
                      <div>
                        <button type="button" onClick={() => { setShowAddForm(false); setSourceBookingId(null); setCreatedInvitationRow(null); }} className="adm-btn adm-btn-secondary">Close</button>
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    <button type="submit" form="add-invitation-form" disabled={loading} className="adm-btn adm-btn-primary" style={{ flex: 1 }}>
                      {loading ? 'Creating…' : 'Create Invitation'}
                    </button>
                    <button type="button" onClick={() => { setShowAddForm(false); setSourceBookingId(null); setCreatedInvitationRow(null); }} className="adm-btn adm-btn-secondary">Cancel</button>
                  </>
                );
              })()}
            </div>
          </aside>
        </>
      )}

      {/* Regenerate modal */}
      {regenerateTargetId && (
        <div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && closeRegenerateConfirmation()}>
          <div className="adm-modal">
            <div className="adm-modal-icon adm-modal-icon--warning">🔑</div>
            <h2 className="adm-modal-title">Regenerate Access Code</h2>
            <p className="adm-modal-body">
              This will invalidate the current code for <strong>{regenerateTargetId}</strong>. Guests who already have the old code will no longer be able to access the seat checker.
              <br /><br />Type <strong>{regenerateTargetId}</strong> to confirm.
            </p>
            <form onSubmit={confirmRegenerateCode} className="adm-modal-form">
              <div className="adm-field">
                <input className="adm-input" type="text" value={regenerateConfirmationInput} onChange={(e) => setRegenerateConfirmationInput(e.target.value)} placeholder={`Type: ${regenerateTargetId}`} autoFocus required />
              </div>
              {error && <div className="adm-alert adm-alert-error">{error}</div>}
              <div className="adm-modal-actions">
                <button type="button" onClick={closeRegenerateConfirmation} disabled={loading} className="adm-btn adm-btn-secondary">Cancel</button>
                <button type="submit" disabled={loading} className="adm-btn adm-btn-primary">{loading ? 'Regenerating…' : 'Confirm Regenerate'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RSVP Close confirmation modal */}
      {rsvpCloseTarget && (
        <div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && (setRsvpCloseTarget(null), setRsvpCloseConfirmationInput(''))}>
          <div className="adm-modal">
            <div className="adm-modal-icon adm-modal-icon--warning">✕</div>
            <h2 className="adm-modal-title">Close RSVP?</h2>
            <p className="adm-modal-body">
              Guests will no longer be able to submit RSVPs for <strong>{rsvpCloseTarget.invitationId}</strong>. You can re-open it at any time.
              <br /><br />Type <strong>{rsvpCloseTarget.invitationId}</strong> to confirm.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); if (rsvpCloseConfirmationInput.trim() !== rsvpCloseTarget.invitationId) { setError(`Type "${rsvpCloseTarget.invitationId}" exactly to confirm.`); setResultMessage(''); setAuthMessage(''); return; } toggleRsvpClosed(rsvpCloseTarget, true); }} className="adm-modal-form">
              <div className="adm-field">
                <input className="adm-input" type="text" value={rsvpCloseConfirmationInput} onChange={(e) => setRsvpCloseConfirmationInput(e.target.value)} placeholder={`Type: ${rsvpCloseTarget.invitationId}`} autoFocus required />
              </div>
              {error && <div className="adm-alert adm-alert-error">{error}</div>}
              <div className="adm-modal-actions">
                <button type="button" onClick={() => { setRsvpCloseTarget(null); setRsvpCloseConfirmationInput(''); }} disabled={loading} className="adm-btn adm-btn-secondary">Cancel</button>
                <button type="submit" disabled={loading} className="adm-btn adm-btn-danger">{loading ? 'Closing…' : 'Close RSVP'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking action confirmation modal */}
      {bookingActionTarget && bookingActionCopy && (
        <div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && closeBookingActionConfirmation()}>
          <div className="adm-modal">
            <div className={`adm-modal-icon ${bookingActionCopy.danger ? 'adm-modal-icon--danger' : 'adm-modal-icon--warning'}`}>
              {bookingActionCopy.danger ? '!' : 'i'}
            </div>
            <h2 className="adm-modal-title">{bookingActionCopy.title}</h2>
            <p className="adm-modal-body">
              {bookingActionCopy.body}
              <br /><br />
              Booking <strong>#{bookingActionTarget.booking.id}</strong>
              {bookingActionTarget.booking.full_name ? <> for <strong>{bookingActionTarget.booking.full_name}</strong></> : null}
            </p>
            <div className="adm-modal-actions">
              <button
                type="button"
                onClick={closeBookingActionConfirmation}
                disabled={Boolean(bookingActionLoading)}
                className="adm-btn adm-btn-secondary"
              >
                Keep Booking
              </button>
              <button
                type="button"
                onClick={confirmBookingAction}
                disabled={Boolean(bookingActionLoading)}
                className={`adm-btn ${bookingActionCopy.danger ? 'adm-btn-danger' : 'adm-btn-primary'}`}
              >
                {bookingActionLoading ? 'Working...' : bookingActionCopy.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking status confirmation modal */}
      {statusChangeTarget && (
        <div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setStatusChangeTarget(null)}>
          <div className="adm-modal">
            <div className="adm-modal-icon adm-modal-icon--warning">!</div>
            <h2 className="adm-modal-title">Confirm Status Change</h2>
            <p className="adm-modal-body">
              Change booking <strong>#{statusChangeTarget.id}</strong>
              {statusChangeTarget.clientName ? <> for <strong>{statusChangeTarget.clientName}</strong></> : null}
              {' '}from <strong>{getBookingStatusOption(statusChangeTarget.currentStatus).label}</strong> to <strong>{getBookingStatusOption(statusChangeTarget.nextStatus).label}</strong>?
            </p>
            <div className="adm-modal-actions">
              <button
                type="button"
                onClick={() => setStatusChangeTarget(null)}
                disabled={bookingStatusUpdating === statusChangeTarget.id}
                className="adm-btn adm-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmBookingStatusChange}
                disabled={bookingStatusUpdating === statusChangeTarget.id}
                className="adm-btn adm-btn-primary"
              >
                {bookingStatusUpdating === statusChangeTarget.id ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deleteTargetRow && (<div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && closeDeleteConfirmation()}>
        <div className="adm-modal">
          <div className="adm-modal-icon adm-modal-icon--danger">🗑</div>
          <h2 className="adm-modal-title">Delete Invitation</h2>
          <p className="adm-modal-body">
            This will permanently remove <strong>{deleteTargetRow.invitationId}</strong>, its access code, all related RSVPs, and the generated JSX file. This action cannot be undone.
            <br /><br />Type <strong>{deleteTargetRow.invitationId}</strong> to confirm.
          </p>
          <form onSubmit={confirmDeleteInvitation} className="adm-modal-form">
            <div className="adm-field">
              <input className="adm-input" type="text" value={deleteConfirmationInput} onChange={(e) => setDeleteConfirmationInput(e.target.value)} placeholder={`Type: ${deleteTargetRow.invitationId}`} autoFocus required />
            </div>
            {error && <div className="adm-alert adm-alert-error">{error}</div>}
            <div className="adm-modal-actions">
              <button type="button" onClick={closeDeleteConfirmation} disabled={loading} className="adm-btn adm-btn-secondary">Cancel</button>
              <button type="submit" disabled={loading} className="adm-btn adm-btn-danger">{loading ? 'Deleting…' : 'Confirm Delete'}</button>
            </div>
          </form>
        </div>
      </div>
      )}

      {/* ══ Edit Offcanvas Drawer ════════════════════════════════════ */}
      {editDrawerRow && (
        <>
          <div className="adm-offcanvas-backdrop" onClick={closeEditDrawer} aria-hidden="true" />
          <aside className="adm-offcanvas" role="dialog" aria-modal="true" aria-label="Edit Invitation">
            <div className="adm-offcanvas-header">
              <div style={{ minWidth: 0 }}>
                <p className="adm-offcanvas-eyebrow">Edit Invitation</p>
                <h2 className="adm-offcanvas-title">{editDrawerRow.title || editDrawerRow.invitationId}</h2>
              </div>
              <button type="button" className="adm-offcanvas-close" onClick={closeEditDrawer} aria-label="Close">✕</button>
            </div>

            <div className="adm-offcanvas-body">
              {/* Meta strip */}
              <div className="adm-offcanvas-meta" style={{ padding: 0, marginBottom: '-4px' }}>
                {editDrawerRow.categoryLabel && (
                  <span className="adm-table-badge">{editDrawerRow.categoryLabel}</span>
                )}
                <code className="adm-offcanvas-id">{editDrawerRow.invitationId}</code>
                {editDrawerRow.previewPath && (
                  <a href={editDrawerRow.previewPath} target="_blank" rel="noreferrer" className="adm-offcanvas-preview-link">
                    👁 Preview ↗
                  </a>
                )}
              </div>

              <form id="edit-invitation-form" onSubmit={saveEditDrawer} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div className="adm-field">
                  <label className="adm-label" htmlFor="oc-title">Invitation Title</label>
                  <input
                    id="oc-title"
                    className="adm-input"
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Juan & Maria's Wedding"
                    required
                    autoFocus
                  />
                </div>

                <div className="adm-field">
                  <label className="adm-label" htmlFor="oc-email">RSVP Email</label>
                  <input
                    id="oc-email"
                    className="adm-input"
                    type="email"
                    value={editForm.rsvpEmail}
                    onChange={(e) => setEditForm((p) => ({ ...p, rsvpEmail: e.target.value }))}
                    placeholder="event@domain.com"
                  />
                  <p className="adm-helper">RSVP submissions from guests go to this address.</p>
                </div>

                <div className="adm-field">
                  <label className="adm-label" htmlFor="oc-date">Event Date</label>
                  <input
                    id="oc-date"
                    className="adm-input"
                    type="date"
                    value={editForm.eventDate}
                    onChange={(e) => setEditForm((p) => ({ ...p, eventDate: e.target.value }))}
                  />
                  <p className="adm-helper">RSVPs auto-close after this date. Leave blank to keep open.</p>
                </div>

                {editError && <div className="adm-alert adm-alert-error">{editError}</div>}
                {editMessage && <div className="adm-alert adm-alert-success">{editMessage}</div>}
              </form>
            </div>

            <div className="adm-offcanvas-footer">
              <button type="submit" form="edit-invitation-form" disabled={editSaving} className="adm-btn adm-btn-primary" style={{ flex: 1 }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" onClick={closeEditDrawer} disabled={editSaving} className="adm-btn adm-btn-secondary">
                Cancel
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ══ Toast notification ══════════════════════════════════════ */}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`} role="alert">
          <span className="adm-toast-icon">
            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="adm-toast-msg">{toast.msg}</span>
          <button type="button" className="adm-toast-close" onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Review delete modal */}
      {deleteReviewTarget && (
        <div className="adm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setDeleteReviewTarget(null)}>
          <div className="adm-modal">
            <div className="adm-modal-icon adm-modal-icon--danger">🗑</div>
            <h2 className="adm-modal-title">Delete Review</h2>
            <p className="adm-modal-body">
              Delete review from <strong>{reviews.find(r => r.id === deleteReviewTarget)?.reviewer_name || 'this customer'}</strong>? This will permanently remove the review and cannot be undone.
            </p>
            <div className="adm-modal-actions">
              <button type="button" onClick={() => setDeleteReviewTarget(null)} disabled={reviewActionLoading === deleteReviewTarget} className="adm-btn adm-btn-secondary">Cancel</button>
              <button type="button" onClick={confirmDeleteReview} disabled={reviewActionLoading === deleteReviewTarget} className="adm-btn adm-btn-danger">
                {reviewActionLoading === deleteReviewTarget ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Manual Review Modal ══════════════════════════════════════ */}
      {showManualReview && (
        <div className="adm-modal-backdrop adm-modal-backdrop--wide" onClick={(e) => e.target === e.currentTarget && closeManualReviewModal()}>
          <div className="adm-modal adm-modal--wide">
            <div className="adm-mr-modal-header">
              <div className="adm-modal-icon adm-modal-icon--star">⭐</div>
              <div>
                <h2 className="adm-modal-title">Add Manual Review</h2>
                <p className="adm-modal-subtitle">Create a published review for a completed booking</p>
              </div>
              <button type="button" className="adm-mr-close-btn" onClick={closeManualReviewModal} aria-label="Close">✕</button>
            </div>

            <form onSubmit={submitManualReview} className="adm-mr-form">

              {/* Booking selector */}
              <div className="adm-mr-section">
                <h3 className="adm-mr-section-title">1 · Select Completed Booking</h3>
                {completedBookingsLoading ? (
                  <div className="adm-loading" style={{ padding: '12px 0' }}>Loading bookings…</div>
                ) : (
                  <select
                    className="adm-input"
                    value={manualForm.bookingId}
                    onChange={e => handleManualBookingSelect(e.target.value)}
                    required
                  >
                    <option value="">— Choose a completed booking —</option>
                    {completedBookings.map(bk => (
                      <option key={bk.id} value={bk.id}>
                        #{bk.id} · {bk.full_name} · {bk.event_type || 'Event'}{bk.template_name ? ` · ${bk.template_name}` : ''} · {bk.event_date ? bk.event_date.slice(0, 10) : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Reviewer info */}
              <div className="adm-mr-section">
                <h3 className="adm-mr-section-title">2 · Reviewer Info</h3>
                <div className="adm-mr-row">
                  <div className="adm-field">
                    <label className="adm-label">Reviewer Name *</label>
                    <input className="adm-input" type="text" value={manualForm.reviewerName} onChange={e => setManualForm(p => ({ ...p, reviewerName: e.target.value }))} placeholder="e.g. Maria Santos" required />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Email <span style={{ fontWeight: 400, color: 'var(--adm-text-subtle)' }}>optional</span></label>
                    <input className="adm-input" type="email" value={manualForm.reviewerEmail} onChange={e => setManualForm(p => ({ ...p, reviewerEmail: e.target.value }))} placeholder="reviewer@email.com" />
                  </div>
                </div>
                <div className="adm-mr-row">
                  <div className="adm-field">
                    <label className="adm-label">Event Type</label>
                    <input className="adm-input" type="text" value={manualForm.eventType} onChange={e => setManualForm(p => ({ ...p, eventType: e.target.value }))} placeholder="e.g. Wedding" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Template Name</label>
                    <input className="adm-input" type="text" value={manualForm.templateName} onChange={e => setManualForm(p => ({ ...p, templateName: e.target.value }))} placeholder="e.g. Blessed Journey" />
                  </div>
                  <div className="adm-field">
                    <label className="adm-label">Event Date *</label>
                    <input className="adm-input" type="date" value={manualForm.eventDate} onChange={e => setManualForm(p => ({ ...p, eventDate: e.target.value }))} required />
                  </div>
                </div>
              </div>

              {/* Ratings */}
              <div className="adm-mr-section">
                <h3 className="adm-mr-section-title">3 · Ratings</h3>
                <div className="adm-mr-ratings-grid">
                  {[
                    { key: 'rating', label: 'Overall *' },
                    { key: 'ratingDesign', label: 'Design' },
                    { key: 'ratingCommunication', label: 'Communication' },
                    { key: 'ratingValue', label: 'Value for Money' },
                  ].map(({ key, label }) => (
                    <div key={key} className="adm-mr-star-group">
                      <span className="adm-mr-star-label">{label}</span>
                      <div className="adm-mr-stars">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} type="button"
                            className={`adm-mr-star-btn${n <= manualForm[key] ? ' active' : ''}`}
                            onClick={() => setManualForm(p => ({ ...p, [key]: p[key] === n ? 0 : n }))}
                            title={`${n} star${n > 1 ? 's' : ''}`}
                          >★</button>
                        ))}
                        {manualForm[key] > 0 && (
                          <span className="adm-mr-star-val">
                            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][manualForm[key]]}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div className="adm-mr-section">
                <h3 className="adm-mr-section-title">4 · Written Review <span style={{ fontWeight: 400, color: 'var(--adm-text-subtle)', fontSize: '0.8rem' }}>(optional)</span></h3>
                <textarea
                  className="adm-input adm-mr-comment"
                  rows={4}
                  maxLength={1000}
                  placeholder="Customer's written feedback…"
                  value={manualForm.comment}
                  onChange={e => setManualForm(p => ({ ...p, comment: e.target.value }))}
                />
                <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#9a8a7e', marginTop: '3px' }}>{manualForm.comment.length} / 1000</div>
              </div>

              {/* Image upload */}
              <div className="adm-mr-section">
                <h3 className="adm-mr-section-title">5 · Photos <span style={{ fontWeight: 400, color: 'var(--adm-text-subtle)', fontSize: '0.8rem' }}>(up to 3, optional)</span></h3>
                <div className="adm-mr-upload-area">
                  {manualImagePreviews.map((src, idx) => (
                    <div key={idx} className="adm-mr-thumb">
                      <img src={src} alt={`Preview ${idx + 1}`} />
                      <button type="button" className="adm-mr-thumb-remove" onClick={() => removeManualImage(idx)} aria-label="Remove">✕</button>
                    </div>
                  ))}
                  {manualImages.length < 3 && (
                    <label className="adm-mr-upload-btn">
                      <span className="adm-mr-upload-icon">📷</span>
                      <span>Add Photo</span>
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleManualImageChange} />
                    </label>
                  )}
                </div>
                <p className="adm-helper" style={{ marginTop: '6px' }}>JPEG, PNG, WebP — max 5 MB each.</p>
              </div>

              {manualReviewError && <div className="adm-alert adm-alert-error">{manualReviewError}</div>}

              <div className="adm-mr-footer">
                <button type="button" onClick={closeManualReviewModal} disabled={manualReviewSubmitting} className="adm-btn adm-btn-secondary">Cancel</button>
                <button type="submit" disabled={manualReviewSubmitting || !manualForm.rating || !manualForm.bookingId} className="adm-btn adm-btn-primary">
                  {manualReviewSubmitting ? 'Publishing…' : '✅ Publish Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

